import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, Search, X, ChevronDown, Trash2, FileText,
  Calendar, User, Package, ChevronRight, ChevronLeft, AlertTriangle,
  CheckCircle, CheckCircle2, Clock, ArrowRight, Edit2, RotateCcw,
  CornerDownLeft, Ban,
  Upload, CreditCard, Send, Users, PenLine, Image,
  PenTool, Tablet, Mail, Link as LinkIcon,
  Receipt, Loader2, Euro, RefreshCw,
  CloudSnow, Wrench, StopCircle, Printer, AlignLeft, Layers, Eye,
  ArrowUp, ArrowDown, ArrowUpDown, Check, LogOut, Truck, Copy, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';
import { useAuth } from '../context/AuthContext';
import { Monitor } from 'lucide-react';
import { SidePanel } from '../components/SidePanel';
import { ClientSearchField } from '../components/ClientSearchField';
import DocumentsTab from '../components/DocumentsTab';
import ReglementPanel from '../components/ReglementPanel';
import * as Dialog from '@radix-ui/react-dialog';
import { ActivitesFichePanel, useHasActivite, ActivityPulse } from '../components/ActivitesFichePanel';import { ModalNouvelleDiscussion } from '../components/ModalNouvelleDiscussion';
import EmailComposeModal from '../components/EmailComposeModal';
import { BlocageClientModal } from '../components/BlocageClientModal';

// ─── Constantes statuts ───────────────────────────────────────────────────────

const STATUTS = [
  { value: 'devis',     label: 'Devis',     bg: 'var(--bg-subtle)', color: 'var(--text-muted)' },
  { value: 'reserve',   label: 'Réservé',   bg: '#EFF6FF',          color: '#1D4ED8' },
  { value: 'confirme',  label: 'Confirmé',  bg: '#FEFCE8',          color: '#A16207' },
  { value: 'en_cours',  label: 'Enlevé',    bg: '#FFF7ED',          color: '#C2410C' },
  { value: 'retour',    label: 'Retourné',  bg: '#F0FDF4',          color: '#15803D' },
  { value: 'annule',    label: 'Annulé',    bg: '#FEF2F2',          color: '#DC2626' },
];

const TRANSITIONS = {
  devis:    [{ to: 'reserve',  label: 'Réserver',            color: '#1D4ED8' }, { to: 'annule', label: 'Annuler', color: '#DC2626' }],
  reserve:  [{ to: 'annule',   label: 'Annuler le contrat',   color: '#DC2626' }],
  confirme: [{ to: 'en_cours', label: 'Enlever (livraison)',  color: '#C2410C' }, { to: 'annule', label: 'Annuler', color: '#DC2626' }],
  en_cours: [{ to: 'retour',   label: 'Retour (check-in)',    color: '#15803D' }],
  retour:   [],
  annule:   [{ to: 'devis', label: 'Remettre en devis', color: '#6D28D9' }],
};

const statutInfo = (val) => STATUTS.find(s => s.value === val) || STATUTS[0];

function fmtEur(v) {
  return Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}
function fmtDate(d) {
  if (!d) return '—';
  const part = d.split('T')[0];
  const [y, m, day] = part.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtDateDMY(d) {
  if (!d) return '—';
  const p = d.split('T')[0].split('-');
  return `${p[2]}/${p[1]}/${p[0]}`;
}

// ── TVA resolution (même logique que Ventes.js) ───────────────────────────────
function _resolveTauxTva(tvaTypeId, tvaRates) {
  if (tvaTypeId && tvaRates?.length > 0) {
    const match = tvaRates.find(r => r.tva_type_id === tvaTypeId);
    if (match) return match.taux;
  }
  return tvaRates?.length > 0 ? tvaRates[0].taux : 20;
}

const SVC_TYPE_OPTS = [
  { value: '',           label: 'Tous types' },
  { value: 'prestation', label: 'Prestation' },
  { value: 'texte',      label: 'Commentaire' },
  { value: 'section',    label: 'Section' },
];
const SVC_TYPE_COLOR = {
  prestation: { bg: '#F0FDF4', color: '#16A34A' },
  texte:      { bg: '#FFFBEB', color: '#B45309' },
  section:    { bg: '#EEF2FF', color: '#4F46E5' },
};
// ─── Helpers de dates (sans décalage timezone UTC→local) ─────────────────────

function parseLocalDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
}

function calcDuree(d1, d2) {
  try {
    const a = parseLocalDate(d1), b = parseLocalDate(d2);
    return Math.max(1, Math.round((b - a) / 86400000) + 1);
  } catch { return 1; }
}
function getPrixJour(duree, grille) {
  if (!grille?.length) return null;
  const sorted = [...grille].sort((a, b) => a.duree_min - b.duree_min);
  for (const t of sorted) {
    if (duree >= t.duree_min && (t.duree_max == null || duree <= t.duree_max)) return t.prix_jour;
  }
  return null;
}

// ─── FilterDropdown ───────────────────────────────────────────────────────────

function FilterDropdown({ label, options, selected, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  const n = selected.length;
  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button onClick={() => setOpen(v => !v)} data-testid={testId}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm whitespace-nowrap"
        style={{ borderColor: n > 0 ? 'var(--primary-solid)' : 'var(--text-disabled)', color: n > 0 ? 'var(--primary-solid)' : 'var(--text-secondary)', background: n > 0 ? 'var(--primary-50)' : 'white' }}>
        {label}
        {n > 0 && <span className="px-1.5 py-0.5 rounded-full text-xs font-bold text-white" style={{ background: 'var(--primary-solid)' }}>{n}</span>}
        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-xl border shadow-xl z-40 py-1" style={{ borderColor: 'var(--border)', minWidth: 180 }}>
          {n > 0 && <button onClick={() => onChange([])} className="w-full px-3 py-1.5 text-left text-xs flex items-center gap-1.5 hover:bg-gray-50" style={{ color: 'var(--text-muted)' }}><X size={10} /> Tout effacer</button>}
          <div className="max-h-60 overflow-y-auto">
            {options.map(o => (
              <label key={o.value} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-gray-50">
                <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} className="w-3.5 h-3.5 rounded" style={{ accentColor: 'var(--primary-solid)' }} />
                {o.dot && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: o.dot }} />}
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



// ─── Matériel label helper ────────────────────────────────────────────────────

function matLabel(m) {
  const parts = [m.nom, m.marque, m.modele].filter(Boolean).join(' ');
  return m.numero_parc ? `${m.numero_parc} - ${parts}` : parts || '—';
}

// ─── Dropdown matériel cherchable ─────────────────────────────────────────────

function MaterielSelect({ materiels, value, onChange, excludedIds = [] }) {
  const [open, setOpen] = useState(false);
  const [q, setQ]       = useState('');
  const ref             = useRef(null);
  const inputRef        = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else setQ('');
  }, [open]);

  const filtered = materiels.filter(m => {
    // Toujours afficher le matériel déjà sélectionné sur CETTE ligne
    if (m.materiel_id === value) return true;
    // Exclure les matériels déjà sélectionnés dans les autres lignes
    if (excludedIds.includes(m.materiel_id)) return false;
    if (!q) return true;
    return matLabel(m).toLowerCase().includes(q.toLowerCase());
  });

  const selected = materiels.find(m => m.materiel_id === value);

  return (
    <div className="relative flex-1" ref={ref}>
      <button type="button" onClick={() => setOpen(v => !v)} data-testid="ligne-materiel-select"
        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg border text-sm text-left"
        style={{ borderColor: 'var(--border)', background: 'white' }}>
        <span className="flex items-center gap-1.5 flex-1 min-w-0" style={{ color: selected ? 'var(--text)' : 'var(--text-disabled)' }}>
          {selected ? (
            <>
              <span className="truncate">{matLabel(selected)}</span>
              {selected.agence_origine_nom && (
                <span className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded font-semibold" style={{ background: '#FFF7ED', color: '#C2410C' }}>
                  {selected.agence_origine_nom}
                </span>
              )}
            </>
          ) : '— À définir à la livraison —'}
        </span>
        <ChevronDown size={13} className={`flex-shrink-0 ml-1 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: 'var(--text-muted)' }} />
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border shadow-xl z-50 overflow-hidden"
          style={{ borderColor: 'var(--border)' }}>
          <div className="p-2 border-b" style={{ borderColor: 'var(--border)' }}>
            <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
              placeholder="Rechercher N° parc, nom, marque..."
              className="w-full px-2.5 py-1.5 rounded-lg border text-sm outline-none"
              style={{ borderColor: 'var(--border)' }} />
          </div>
          <div className="max-h-52 overflow-y-auto">
            <button type="button" onClick={() => { onChange(null); setOpen(false); }}
              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 border-b"
              style={{ borderColor: 'var(--border-light)', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              — À définir à la livraison —
            </button>
            {filtered.length === 0
              ? <p className="px-3 py-3 text-sm text-center" style={{ color: 'var(--text-muted)' }}>Aucun résultat</p>
              : filtered.map(m => (
                <button key={m.materiel_id} type="button" disabled={!m.disponible}
                  onClick={() => { onChange(m.materiel_id); setOpen(false); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 border-b disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-between gap-2"
                  style={{ borderColor: 'var(--border-light)', color: 'var(--text)' }}>
                  <span className="flex items-center gap-1.5 flex-1 min-w-0">
                    {!m.disponible && <span className="text-red-400 flex-shrink-0">✗</span>}
                    <span className="truncate">{matLabel(m)}</span>
                  </span>
                  {m.agence_origine_nom && (
                    <span className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded font-semibold" style={{ background: '#FFF7ED', color: '#C2410C' }}>
                      {m.agence_origine_nom}
                    </span>
                  )}
                </button>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── LigneMatériel editor ─────────────────────────────────────────────────────

const JOURS_SEMAINE = [
  { idx: 0, label: 'L' },
  { idx: 1, label: 'Ma' },
  { idx: 2, label: 'Me' },
  { idx: 3, label: 'J' },
  { idx: 4, label: 'V' },
  { idx: 5, label: 'S' },
  { idx: 6, label: 'D' },
];

// Calcul durée côté client (hors jours fériés — ceux-ci sont gérés par le backend)
function calcDureeClient(d1str, d2str, joursExcl, feriesSet = null) {
  if (!d1str || !d2str) return null;
  try {
    const d1 = parseLocalDate(d1str);
    const d2 = parseLocalDate(d2str);
    const excSet = new Set(joursExcl || []);
    let count = 0;
    const cur = new Date(d1);
    while (cur <= d2) {
      const wd = cur.getDay() === 0 ? 6 : cur.getDay() - 1; // 0=Lun…6=Dim
      const iso = `${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,'0')}-${String(cur.getDate()).padStart(2,'0')}`;
      if (!excSet.has(wd) && !(feriesSet && feriesSet.has(iso))) count++;
      cur.setDate(cur.getDate() + 1);
    }
    return Math.max(1, count);
  } catch { return null; }
}

function GammeCombobox({ gammes, value, onChange }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const ref               = useRef(null);

  const sorted  = useMemo(
    () => [...gammes].sort((a, b) =>
      `${a.categorie_nom} — ${a.nom}`.localeCompare(`${b.categorie_nom} — ${b.nom}`, 'fr', { sensitivity: 'base', numeric: true })
    ),
    [gammes]
  );

  const filtered = query.trim()
    ? sorted.filter(g =>
        `${g.categorie_nom} ${g.nom}`.toLowerCase().includes(query.toLowerCase())
      )
    : sorted;

  const selected = sorted.find(g => g.gamme_id === value);
  const label    = selected ? `${selected.categorie_nom} — ${selected.nom}` : 'Sélectionner…';

  // Fermer au clic extérieur
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = (gamme_id) => {
    onChange(gamme_id);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={ref} className="relative flex-1" data-testid="gamme-combobox">
      <button
        type="button"
        onClick={() => { setOpen(o => !o); setQuery(''); }}
        data-testid="ligne-gamme-select"
        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg border text-sm text-left"
        style={{ borderColor: 'var(--border)', background: 'white', color: value ? 'var(--text)' : 'var(--text-muted)' }}>
        <span className="truncate">{label}</span>
        <ChevronDown size={14} className="flex-shrink-0 ml-1" style={{ color: 'var(--text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border shadow-lg overflow-hidden"
          style={{ borderColor: 'var(--border)', background: 'white', maxHeight: 520 }}>
          <div className="p-1.5 border-b" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md" style={{ background: 'var(--bg-subtle)' }}>
              <Search size={12} style={{ color: 'var(--text-muted)' }} />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Rechercher une gamme…"
                data-testid="gamme-search-input"
                className="flex-1 text-sm bg-transparent outline-none"
                style={{ color: 'var(--text)' }}
              />
              {query && <button type="button" onClick={() => setQuery('')}><X size={11} style={{ color: 'var(--text-muted)' }} /></button>}
            </div>
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 460 }}>
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-sm" style={{ color: 'var(--text-muted)' }}>Aucun résultat</div>
            )}
            {filtered.map(g => (
              <button
                key={g.gamme_id}
                type="button"
                onMouseDown={() => handleSelect(g.gamme_id)}
                data-testid={`gamme-option-${g.gamme_id}`}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 transition-colors"
                style={{ color: g.gamme_id === value ? 'var(--primary-solid)' : 'var(--text)', fontWeight: g.gamme_id === value ? 600 : 400 }}>
                {g.categorie_nom} — {g.nom}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ArticleCombobox — sélecteur cherchable d'articles prestation ─────────────
function ArticleCombobox({ articles, value, onChange }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const ref               = useRef(null);

  const sorted = useMemo(
    () => [...articles].sort((a, b) =>
      (a.designation || '').localeCompare(b.designation || '', 'fr', { numeric: true })
    ),
    [articles]
  );

  const filtered = query.trim()
    ? sorted.filter(a => (a.designation || '').toLowerCase().includes(query.toLowerCase()))
    : sorted;

  const selected = sorted.find(a => a.article_id === value);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div ref={ref} className="relative flex-1" data-testid="article-combobox">
      <button
        type="button"
        onClick={() => { setOpen(o => !o); setQuery(''); }}
        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg border text-sm text-left"
        style={{ borderColor: 'var(--border)', background: 'white', color: selected ? 'var(--text)' : 'var(--text-muted)' }}>
        <span className="truncate">{selected ? selected.designation : 'Prestation (optionnel)'}</span>
        <ChevronDown size={14} className="flex-shrink-0 ml-1" style={{ color: 'var(--text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border shadow-lg overflow-hidden"
          style={{ borderColor: 'var(--border)', background: 'white', maxHeight: 320 }}>
          <div className="p-1.5 border-b" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md" style={{ background: 'var(--bg-subtle)' }}>
              <Search size={12} style={{ color: 'var(--text-muted)' }} />
              <input autoFocus type="text" value={query} onChange={e => setQuery(e.target.value)}
                placeholder="Rechercher une prestation…"
                className="flex-1 text-sm bg-transparent outline-none" style={{ color: 'var(--text)' }} />
              {query && <button type="button" onClick={() => setQuery('')}><X size={11} style={{ color: 'var(--text-muted)' }} /></button>}
            </div>
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 260 }}>
            <button type="button" onMouseDown={() => { onChange(null); setOpen(false); setQuery(''); }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 transition-colors"
              style={{ color: 'var(--text-muted)' }}>
              — Aucune prestation —
            </button>
            {filtered.length === 0
              ? <div className="px-3 py-2 text-sm" style={{ color: 'var(--text-muted)' }}>Aucun résultat</div>
              : filtered.map(a => (
                <button key={a.article_id} type="button"
                  onMouseDown={() => { onChange(a); setOpen(false); setQuery(''); }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 transition-colors"
                  style={{ color: a.article_id === value ? 'var(--primary-solid)' : 'var(--text)', fontWeight: a.article_id === value ? 600 : 400 }}>
                  {a.designation}
                </button>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SvcAddRow — ligne fantôme de recherche d'article prestation ─────────────
function SvcAddRow({ onSelect, colSpan = 8 }) {
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [open, setOpen]           = useState(false);
  const [loading, setLoading]     = useState(false);
  const [pos, setPos]             = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const inputRef  = useRef();
  const rowRef    = useRef();

  const search = useCallback(async (q, type) => {
    setLoading(true);
    try {
      const params = { limit: 40, sort_by: 'reference', sort_dir: 1, type: 'prestation' };
      if (q && q.trim()) params.q = q.trim();
      if (type && type !== 'prestation') params.type = undefined;
      const r = await api.getArticles(params);
      setResults(r.data?.items || []);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query, typeFilter), 250);
    return () => clearTimeout(t);
  }, [query, typeFilter, search]);

  useEffect(() => {
    const h = (e) => {
      if (rowRef.current && !rowRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const calcPos = () => {
    if (!inputRef.current) return;
    const r = inputRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 480) });
  };

  const select = (art) => { onSelect(art); setQuery(''); setResults([]); setOpen(false); setTypeFilter(''); };

  const isSpecial  = typeFilter === 'texte' || typeFilter === 'section';
  const placeholder = typeFilter === 'section' ? 'Titre de la section…'
    : typeFilter === 'texte' ? 'Saisissez le commentaire…'
    : 'Rechercher une prestation…';

  const handleKeyDown = (e) => {
    if (isSpecial && e.key === 'Enter' && query.trim()) {
      e.preventDefault();
      select({ type_ligne: typeFilter, designation: query });
    }
    if (!isSpecial && e.key === 'Enter' && results.length > 0) select(results[0]);
  };

  const showDropdown = !isSpecial && open && pos && (results.length > 0 || loading);

  return (
    <tr ref={rowRef} style={{ borderTop: '1px solid var(--border)' }}>
      <td colSpan={colSpan} className="px-3 py-2">
        <div className="flex items-center gap-2">
          <Plus size={13} style={{ color: '#9CA3AF', flexShrink: 0 }} />
          <select value={typeFilter}
            onMouseDown={e => e.stopPropagation()}
            onChange={e => { const v = e.target.value; setTypeFilter(v); setResults([]); setQuery('');
              if (v === 'texte' || v === 'section') { setOpen(false); setTimeout(() => inputRef.current?.focus(), 50); } }}
            className="text-xs border rounded px-1.5 py-1 focus:outline-none flex-shrink-0"
            style={{ borderColor: 'var(--border)', color: '#6B7280', background: '#fff' }}>
            {SVC_TYPE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div className="flex-1 flex items-center gap-1.5 rounded-lg border px-2.5 py-1 focus-within:ring-1 focus-within:ring-blue-400"
            style={{ borderColor: isSpecial ? (typeFilter === 'section' ? '#818CF8' : '#D97706') : 'var(--border)', background: '#fff' }}>
            {isSpecial
              ? (typeFilter === 'section'
                  ? <Layers size={12} style={{ color: '#6366F1', flexShrink: 0 }} />
                  : <AlignLeft size={12} style={{ color: '#D97706', flexShrink: 0 }} />)
              : <Search size={12} style={{ color: '#9CA3AF', flexShrink: 0 }} />}
            <input ref={inputRef} value={query}
              onChange={e => { setQuery(e.target.value); if (!isSpecial) { setOpen(true); calcPos(); } }}
              onFocus={() => { if (!isSpecial) { setOpen(true); calcPos(); if (results.length === 0) search(query, typeFilter); } }}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              data-testid="svc-article-search"
              className="flex-1 text-sm bg-transparent focus:outline-none"
              style={{ color: isSpecial ? (typeFilter === 'section' ? '#3730A3' : '#78350F') : '#374151',
                       fontStyle: typeFilter === 'texte' ? 'italic' : 'normal',
                       fontWeight: typeFilter === 'section' ? 600 : 400 }} />
            {isSpecial && query.trim() && (
              <button type="button" onMouseDown={e => { e.preventDefault(); select({ type_ligne: typeFilter, designation: query }); }}
                className="text-xs px-2 py-0.5 rounded font-medium flex-shrink-0"
                style={{ background: typeFilter === 'section' ? '#EEF2FF' : '#FEF3C7',
                         color: typeFilter === 'section' ? '#4F46E5' : '#92400E' }}>Ajouter</button>
            )}
            {!isSpecial && loading && <Loader2 size={12} className="animate-spin flex-shrink-0" style={{ color: '#9CA3AF' }} />}
          </div>
        </div>
        {/* Dropdown inline — position: fixed pour échapper à overflow:auto du parent */}
        {showDropdown && (
          <div
            data-testid="svc-dropdown"
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999,
                     background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10,
                     boxShadow: '0 8px 24px rgba(0,0,0,.1)', maxHeight: 340, overflowY: 'auto' }}>
            {results.map(a => (
              <button key={a.article_id} type="button"
                data-testid={`svc-article-${a.article_id}`}
                onClick={() => select(a)}
                className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-slate-50 border-b last:border-0"
                style={{ borderColor: '#F1F5F9' }}>
                <span className="text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0"
                  style={SVC_TYPE_COLOR.prestation}>Prestation</span>
                <span className="text-xs font-mono text-slate-400 flex-shrink-0">{a.reference}</span>
                <span className="text-sm text-slate-700 truncate">{a.designation}</span>
                <span className="text-xs text-slate-400 ml-auto flex-shrink-0">{fmtEur(a.prix_vente)}</span>
              </button>
            ))}
            {!loading && results.length === 0 && (
              <div className="px-4 py-3 text-sm text-slate-400">Aucun résultat</div>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

// ─── LigneSvcRow — ligne service dans le tableau ──────────────────────────────
function LigneSvcRow({ l, i, tvaRates, canEdit, onUpdate, onRemove, onToggleFlag }) {
  const isSpecial = l.type_ligne === 'texte' || l.type_ligne === 'section';
  const isAnnule  = !!l.annule;
  const isRetour  = !!l.facturer_au_retour;
  const total     = isAnnule ? 0 : (l.qte || 0) * (l.pu || 0);
  const tc        = isAnnule
    ? { background: '#F3F4F6', color: '#9CA3AF' }
    : SVC_TYPE_COLOR[l.type_ligne || 'prestation'] || SVC_TYPE_COLOR.prestation;
  const typLabel  = l.type_ligne === 'texte' ? 'Commentaire'
    : l.type_ligne === 'section' ? 'Section' : 'Prestation';

  // Dropdown TVA custom (position:fixed pour éviter le piège de focus Radix)
  const [tvaOpen, setTvaOpen] = useState(false);
  const [tvaPos,  setTvaPos]  = useState(null);
  const tvaBtnRef  = useRef(null);
  const tvaDropRef = useRef(null);

  useEffect(() => {
    if (!tvaOpen) return;
    const h = (e) => {
      if (tvaDropRef.current && !tvaDropRef.current.contains(e.target) &&
          tvaBtnRef.current  && !tvaBtnRef.current.contains(e.target)) {
        setTvaOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [tvaOpen]);

  const openTvaDrop = () => {
    if (!canEdit || tvaRates.length === 0) return;
    if (tvaBtnRef.current) {
      const r = tvaBtnRef.current.getBoundingClientRect();
      setTvaPos({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 90) });
    }
    setTvaOpen(o => !o);
  };

  const selectedRate = tvaRates.find(r => r.taux === (l.taux_tva ?? null));
  const tvaLabel = selectedRate
    ? selectedRate.taux.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + ' %'
    : (l.taux_tva != null ? String(l.taux_tva).replace('.', ',') + ' %' : '— %');

  if (isSpecial) {
    return (
      <tr style={{ borderTop: '1px solid var(--border)' }}>
        <td className="px-2 py-1.5" colSpan={2}>
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={tc}>{typLabel}</span>
        </td>
        <td colSpan={5} className="px-2 py-1.5">
          <input value={l.designation || ''} onChange={e => onUpdate('designation', e.target.value)}
            placeholder={l.type_ligne === 'section' ? 'Titre de la section…' : 'Commentaire…'}
            className="w-full text-sm bg-transparent focus:outline-none border-b border-dashed"
            style={{ color: l.type_ligne === 'section' ? '#4F46E5' : '#92400E',
                     fontWeight: l.type_ligne === 'section' ? 600 : 400,
                     fontStyle: l.type_ligne === 'texte' ? 'italic' : 'normal',
                     borderColor: 'var(--border)' }} />
        </td>
        <td className="px-2 py-1.5 text-center">
          {canEdit && <button onClick={onRemove} className="p-1 rounded hover:bg-red-50"><Trash2 size={13} style={{ color: '#EF4444' }} /></button>}
        </td>
      </tr>
    );
  }

  return (
    <tr style={{ borderTop: '1px solid var(--border)', opacity: isAnnule ? 0.55 : 1, background: isAnnule ? '#FAFAFA' : undefined }}>
      {/* ── Colonne flags en tête ── */}
      <td className="px-2 py-1.5">
        {onToggleFlag && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => onToggleFlag(l.ligne_id, 'facturer_au_retour', !isRetour)}
              data-testid={`flag-retour-${l.ligne_id}`}
              title={isRetour ? 'Annuler "facturer au retour"' : 'Facturer seulement au retour du matériel'}
              className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border transition-all"
              style={{
                background: isRetour ? '#DBEAFE' : '#F3F4F6',
                color: isRetour ? '#1D4ED8' : '#6B7280',
                borderColor: isRetour ? '#93C5FD' : 'transparent',
              }}>
              <CornerDownLeft size={11} />
              <span>Au retour</span>
            </button>
            <button
              onClick={() => onToggleFlag(l.ligne_id, 'annule', !isAnnule)}
              data-testid={`flag-annule-${l.ligne_id}`}
              title={isAnnule ? 'Réactiver la prestation' : 'Annuler la prestation'}
              className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border transition-all"
              style={{
                background: isAnnule ? '#FEE2E2' : '#F3F4F6',
                color: isAnnule ? '#DC2626' : '#6B7280',
                borderColor: isAnnule ? '#FCA5A5' : 'transparent',
              }}>
              {isAnnule ? <RotateCcw size={11} /> : <Ban size={11} />}
              <span>{isAnnule ? 'Réactiver' : 'Annuler'}</span>
            </button>
          </div>
        )}
      </td>
      <td className="px-2 py-1.5">
        <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{l.reference || '—'}</span>
      </td>
      <td className="px-2 py-1.5">
        <input value={l.designation || ''} onChange={e => onUpdate('designation', e.target.value)}
          className="w-full text-sm bg-transparent focus:outline-none" style={{ color: 'var(--text)' }} />
      </td>
      <td className="px-2 py-1.5 text-center">
        <input type="number" min="0.01" step={l.unite_symbole === 'U' ? '1' : '0.01'}
          value={l.qte || 1} onChange={e => onUpdate('qte', Number(e.target.value))}
          className="w-14 text-center text-sm rounded border px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
          style={{ borderColor: 'var(--border)' }} />
      </td>
      <td className="px-2 py-1.5 text-center">
        <input type="number" step="0.01"
          value={l.pu || 0} onChange={e => onUpdate('pu', Number(e.target.value))}
          className="w-20 text-center text-sm rounded border px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
          style={{ borderColor: 'var(--border)' }} />
      </td>
      <td className="px-2 py-1.5 text-center">
        <>
          <button
            ref={tvaBtnRef}
            type="button"
            onClick={canEdit && tvaRates.length > 0 ? openTvaDrop : undefined}
            className="w-20 text-center text-sm rounded border px-1 py-1 inline-flex items-center justify-center gap-1"
            style={{
              borderColor: tvaOpen ? '#6366F1' : 'var(--border)',
              background: 'var(--bg)',
              cursor: canEdit && tvaRates.length > 0 ? 'pointer' : 'default',
              opacity: canEdit ? 1 : 0.7,
            }}
            data-testid={`tva-btn-${i}`}
          >
            {tvaLabel}
            {canEdit && tvaRates.length > 0 && (
              <ChevronDown size={10} style={{ color: 'var(--text-muted)', transform: tvaOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            )}
          </button>
          {tvaOpen && tvaPos && (
            <div
              ref={tvaDropRef}
              style={{ position: 'fixed', top: tvaPos.top, left: tvaPos.left, width: tvaPos.width,
                       zIndex: 9999, background: '#fff', border: '1px solid var(--border)',
                       borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', overflow: 'hidden' }}
            >
              {tvaRates.map(r => (
                <button
                  key={r.tva_type_id}
                  type="button"
                  onMouseDown={() => { onUpdate('taux_tva', r.taux); setTvaOpen(false); }}
                  className="w-full text-center text-sm py-1.5 px-2 hover:bg-indigo-50 transition-colors"
                  style={{
                    background: r.taux === l.taux_tva ? '#EEF2FF' : 'transparent',
                    color:      r.taux === l.taux_tva ? '#4338CA' : 'var(--text)',
                    fontWeight: r.taux === l.taux_tva ? 600 : 400,
                  }}
                  data-testid={`tva-option-${r.taux}`}
                >
                  {r.taux.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} %
                </button>
              ))}
            </div>
          )}
        </>
      </td>
      <td className="px-2 py-1.5 text-center">
        <span className="text-sm font-semibold tabular-nums" style={{ color: isAnnule ? '#9CA3AF' : 'var(--text)', textDecoration: isAnnule ? 'line-through' : 'none' }}>{fmtEur(total)}</span>
      </td>
      <td className="px-2 py-1.5 text-center">
        {canEdit && <button onClick={onRemove} className="p-1 rounded hover:bg-red-50"><Trash2 size={13} style={{ color: '#EF4444' }} /></button>}
      </td>
    </tr>
  );
}

function LigneMaterielForm({ ligne, gammes, onUpdate, onRemove, feriesSet, agenceId, canEdit = true, canEditMateriel = true, canEditPrix = true, excludedMaterielIds = [] }) {
  const [materiels, setMateriels]   = useState([]);

  const gamme = gammes.find(g => g.gamme_id === ligne.gamme_id);
  const selectedMat = materiels.find(m => m.materiel_id === ligne.materiel_id);

  // Durée facturable affichée
  const dureeFacturable = useMemo(
    () => calcDureeClient(
      ligne.date_debut, ligne.date_fin, ligne.jours_exclus,
      ligne.exclure_jours_feries ? feriesSet : null
    ),
    [ligne.date_debut, ligne.date_fin, ligne.jours_exclus, feriesSet, ligne.exclure_jours_feries]
  );

  // Matériels disponibles — chargés dès que la gamme est choisie (dates optionnelles)
  useEffect(() => {
    if (!ligne.gamme_id) { setMateriels([]); return; }
    api.getMaterielsDisponibles({
      gamme_id: ligne.gamme_id,
      ...(agenceId                ? { agence_id:  agenceId               } : {}),
      ...(ligne.date_debut        ? { date_debut: ligne.date_debut        } : {}),
      ...(ligne.date_fin          ? { date_fin:   ligne.date_fin          } : {}),
    })
      .then(r => setMateriels(r.data || []))
      .catch(() => {});
  }, [ligne.gamme_id, ligne.date_debut, ligne.date_fin, agenceId]);

  // Recalcul quand gamme, dates ou jours fériés changent
  useEffect(() => {
    if (!gamme || !ligne.date_debut || !ligne.date_fin) return;
    const effectiveFeries = ligne.exclure_jours_feries ? feriesSet : null;
    const duree = calcDureeClient(ligne.date_debut, ligne.date_fin, ligne.jours_exclus, effectiveFeries);
    if (!duree) return;
    const pj = getPrixJour(duree, gamme.grille_tarifaire);
    const updates = {};
    if (pj !== null) {
      // Ne pas écraser un prix saisi manuellement — uniquement si pas encore de prix ou si la gamme change
      if (!ligne.prix_jour_applique || ligne._gamme_changed) {
        updates.prix_jour_applique = pj;
        updates.montant = Math.round(pj * duree * 100) / 100;
        updates._gamme_changed = false;
      } else {
        // Recalculer seulement le montant avec le prix existant
        updates.montant = Math.round((ligne.prix_jour_applique || pj) * duree * 100) / 100;
      }
    }
    if (!ligne.caution)   updates.caution   = gamme.caution;
    if (!ligne.franchise) updates.franchise  = gamme.franchise;
    if (!ligne.assurance) updates.assurance  = gamme.assurance_renonciation;
    if (Object.keys(updates).length) onUpdate(updates);
  }, [ligne.gamme_id, ligne.date_debut, ligne.date_fin, feriesSet, ligne.exclure_jours_feries]); // eslint-disable-line

  const set = (field, val) => onUpdate({ [field]: val });

  // Mise à jour atomique : jours exclus + montant recalculé en une seule passe
  const toggleJour = (idx) => {
    const current = ligne.jours_exclus || [5, 6];
    const next = current.includes(idx) ? current.filter(j => j !== idx) : [...current, idx];
    const effectiveFeries = ligne.exclure_jours_feries ? feriesSet : null;
    const newDuree = calcDureeClient(ligne.date_debut, ligne.date_fin, next, effectiveFeries);
    const pj = ligne.prix_jour_applique;
    const updates = { jours_exclus: next };
    if (newDuree && pj != null) {
      updates.montant = Math.round(pj * newDuree * 100) / 100;
    }
    onUpdate(updates);
  };

  // Label style helper (inline pattern)
  const LB = ({ children }) => (
    <span className="w-24 flex-shrink-0 text-xs font-semibold text-right pr-2"
      style={{ color: 'var(--text-muted)', lineHeight: '34px' }}>
      {children}
    </span>
  );

  return (
    <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: 'var(--border)' }}>

      {/* Gamme + bouton supprimer */}
      <div className="flex items-center gap-1">
        <LB>Gamme</LB>
        <GammeCombobox
          gammes={gammes}
          value={ligne.gamme_id || ''}
          onChange={val => canEdit && onUpdate({ gamme_id: val, materiel_id: null, caution: null, franchise: null, assurance: null, prix_jour_applique: null, montant: null, _gamme_changed: true })}
          disabled={!canEdit}
        />
        {canEdit && (
          <button onClick={onRemove} className="ml-1 p-1.5 rounded-lg hover:bg-red-50 flex-shrink-0">
            <Trash2 size={14} style={{ color: '#EF4444' }} />
          </button>
        )}
      </div>

      {/* Dates */}
      <div className="flex items-center gap-1">
        <LB>Période</LB>
        <input type="date" value={ligne.date_debut || ''} onChange={e => canEdit && set('date_debut', e.target.value)}
          disabled={!canEdit}
          data-testid="ligne-date-debut"
          className="flex-1 px-2.5 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }} />
        <span className="px-1 text-xs" style={{ color: 'var(--text-muted)' }}>→</span>
        <input type="date" value={ligne.date_fin || ''} onChange={e => canEdit && set('date_fin', e.target.value)}
          disabled={!canEdit}
          required
          data-testid="ligne-date-fin"
          className="flex-1 px-2.5 py-2 rounded-lg border text-sm"
          style={{ borderColor: ligne.gamme_id && !ligne.date_fin ? '#EF4444' : 'var(--border)' }} />
      </div>
      
      {/* Jours exclus */}
      <div className="flex items-center gap-1 flex-wrap">
        <LB>Exclure</LB>
        <div className="flex items-center gap-1 flex-wrap">
          {JOURS_SEMAINE.map(({ idx, label }) => {
            const checked = (ligne.jours_exclus ?? [5, 6]).includes(idx);
            return (
              <button
                key={idx}
                type="button"
                onClick={() => canEdit && toggleJour(idx)}
                disabled={!canEdit}
                data-testid={`jour-excl-${idx}`}
                className="w-8 h-7 rounded-md text-xs font-semibold border transition-all"
                style={{
                  background: checked ? '#FEE2E2' : 'white',
                  color: checked ? '#DC2626' : 'var(--text-muted)',
                  borderColor: checked ? '#FECACA' : 'var(--border)',
                  textDecoration: checked ? 'line-through' : 'none',
                  opacity: canEdit ? 1 : 0.6,
                }}>
                {label}
              </button>
            );
          })}
          <label className="flex items-center gap-1.5 ml-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={ligne.exclure_jours_feries ?? true}
              onChange={e => canEdit && set('exclure_jours_feries', e.target.checked)}
              disabled={!canEdit}
              data-testid="exclure-jours-feries"
              className="w-3.5 h-3.5 rounded"
              style={{ accentColor: '#DC2626' }}
            />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Fériés</span>
          </label>
        </div>
      </div>

      {/* ⚠️ Alerte : aucun jour facturable dans la période */}
      {ligne.date_debut && ligne.date_fin && dureeFacturable === 0 && (() => {
        const jExcl = ligne.jours_exclus ?? [5, 6];
        const NOMS_JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
        // Trouver les jours de la période et identifier ceux qui tombent un jour exclu
        const d1 = new Date(ligne.date_debut + 'T12:00:00');
        const d2 = new Date(ligne.date_fin   + 'T12:00:00');
        const nbJoursCalendrier = Math.round((d2 - d1) / 86400000) + 1;
        const joursTombantsExclus = [];
        for (let i = 0; i < Math.min(nbJoursCalendrier, 7); i++) {
          const d = new Date(d1); d.setDate(d.getDate() + i);
          const wd = (d.getDay() + 6) % 7; // 0=Lun, 6=Dim
          if (jExcl.includes(wd)) joursTombantsExclus.push(NOMS_JOURS[wd]);
        }
        const allExcl = nbJoursCalendrier <= 7 && joursTombantsExclus.length === nbJoursCalendrier;
        const hint = allExcl && joursTombantsExclus.length > 0
          ? `Tous les jours tombent un ${[...new Set(joursTombantsExclus)].join('/')} (exclu).`
          : 'Tous les jours de cette période sont exclus de la facturation.';
        return (
          <div data-testid="alerte-zero-jours-facturables"
            className="flex items-start gap-2 px-3 py-2 rounded-lg text-xs ml-24"
            style={{ background: '#FFF7ED', border: '1px solid #FED7AA', color: '#9A3412' }}>
            <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              <strong>0 jour facturable</strong> — {hint}{' '}
              Modifiez les dates ou décochez les jours exclus ci-dessus.
            </span>
          </div>
        );
      })()}

      {/* Matériel */}
      {ligne.gamme_id && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <LB>Matériel</LB>
            <MaterielSelect
              materiels={materiels}
              value={ligne.materiel_id}
              onChange={(id) => canEditMateriel && set('materiel_id', id)}
              disabled={!canEditMateriel}
              excludedIds={excludedMaterielIds}
            />
          </div>
          {selectedMat?.badge_non_conforme && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg text-xs ml-24"
              style={{ background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E' }}>
              <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>Ce matériel a une <strong>VGP expirée</strong> (contrôle non conforme). Vous pouvez le louer, mais une mise en conformité préalable est recommandée.</span>
            </div>
          )}
        </div>
      )}

      {/* Récapitulatif prix */}
      {ligne.gamme_id && ligne.date_debut && ligne.date_fin && (
        <div className="flex items-center gap-1">
          <LB>Prix</LB>
          <div className="flex items-center gap-2 flex-1">
            <input type="number" min="0" step="0.01" value={ligne.prix_jour_applique || ''}
              onChange={e => {
                if (!canEdit && !canEditPrix) return;
                const pj = Number(e.target.value);
                const d = dureeFacturable || 1;
                onUpdate({ prix_jour_applique: pj, montant: Math.round(pj * d * 100) / 100 });
              }}
              disabled={!canEdit && !canEditPrix}
              data-testid="ligne-prix-jour"
              className="w-24 px-2 py-1.5 rounded-lg border text-sm text-right font-semibold"
              style={{ borderColor: 'var(--border)', opacity: (canEdit || canEditPrix) ? 1 : 0.7 }}
              placeholder="€/j"
            />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>€/j ×</span>
            <span className="text-sm font-bold" style={{ color: 'var(--text)' }}>{dureeFacturable ?? '—'} j</span>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>=</span>
            <span className="text-sm font-bold" style={{ color: 'var(--primary-solid)' }}>{fmtEur(ligne.montant)}</span>
          </div>
        </div>
      )}

      {/* Badges caution/franchise/assurance */}
      {(ligne.caution?.active || ligne.franchise?.active || ligne.assurance?.active) && (
        <div className="flex flex-wrap gap-1 pl-26">
          {ligne.caution?.active && <span className="px-2 py-0.5 rounded-full text-xs" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>Caution {fmtEur(ligne.caution.montant)}</span>}
          {ligne.franchise?.active && <span className="px-2 py-0.5 rounded-full text-xs" style={{ background: '#FFF7ED', color: '#C2410C' }}>Franchise {fmtEur(ligne.franchise.montant)}</span>}
          {ligne.assurance?.active && <span className="px-2 py-0.5 rounded-full text-xs" style={{ background: '#F0FDF4', color: '#15803D' }}>Assurance {fmtEur(ligne.assurance.montant)}</span>}
        </div>
      )}
    </div>
  );
}

// ─── Panneau de création/édition de contrat ────────────────────────────────────

const EMPTY_LIGNE_MAT = { gamme_id: '', materiel_id: null, date_debut: '', date_fin: '', jours_exclus: [5, 6], exclure_jours_feries: true, prix_jour_applique: null, montant: null, caution: null, franchise: null, assurance: null };
const EMPTY_LIGNE_SVC = { article_id: null, designation: '', qte: 1, pu: 0, taux_tva: 0, remise: 0 };

// ─── ValidationRow ────────────────────────────────────────────────────────────

function ValidationRow({ label, checked, pdfPath, pdfNom, uploading, onToggle, onUpload }) {
  const fileRef = useRef(null);
  return (
    <div className="flex items-center gap-3">
      <button type="button" onClick={() => onToggle(!checked)}
        className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center border-2 transition-all"
        style={{
          background: checked ? '#15803D' : 'white',
          borderColor: checked ? '#15803D' : '#D1D5DB',
        }}>
        {checked && <CheckCircle size={12} color="white" />}
      </button>
      <span className="flex-1 text-sm" style={{ color: checked ? 'var(--text)' : 'var(--text-muted)' }}>
        {label}
      </span>
      {onUpload && (
        <>
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden"
            onChange={e => { if (e.target.files[0]) onUpload(e.target.files[0]); e.target.value = ''; }} />
          <button type="button" onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all"
            style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)', background: 'white' }}>
            <Upload size={11} className={uploading ? 'animate-pulse' : ''} />
            {pdfPath ? 'Remplacer' : 'Upload PDF'}
          </button>
          {pdfNom && (
            <span className="text-xs truncate max-w-24" style={{ color: 'var(--text-muted)' }} title={pdfNom}>
              {pdfNom}
            </span>
          )}
        </>
      )}
    </div>
  );
}

// ─── FacturationTab ────────────────────────────────────────────────────────────

const STATUT_FAC_LABELS = {
  // Odoo
  brouillon: { label: 'Brouillon Odoo', bg: '#F3F4F6', color: '#374151' },
  valide:    { label: 'Validée',        bg: '#DCFCE7', color: '#15803D' },
  paye:      { label: 'Payée',          bg: '#D1FAE5', color: '#065F46' },
  annule:    { label: 'Annulée',        bg: '#FEE2E2', color: '#DC2626' },
  // Pennylane
  pl_brouillon: { label: 'Brouillon PL', bg: '#FEF3C7', color: '#92400E' },
  pl_valide:    { label: 'Validée PL',   bg: '#DCFCE7', color: '#15803D' },
  pl_annule:    { label: 'Annulée PL',   bg: '#FEE2E2', color: '#DC2626' },
};

function FacturationTab({ contrat, onContratUpdate }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin_org' || user?.role === 'super_admin';
  const navigate = useNavigate();

  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingFacture, setCreatingFacture] = useState(false);
  const [plAction, setPlAction] = useState({}); // {facture_id: 'validating'|'deleting'}
  const [reglPanel, setReglPanel] = useState(null); // facture_id ouvrant le panel règlement
  const [downloadingAcquittee, setDownloadingAcquittee] = useState(false);
  // Panel brouillon PL (même pattern que AideFacturationLocation / Ventes)
  const [plDraftPanel, setPlDraftPanel]           = useState(null); // null | { loading, pdf_blob_url }
  const [validantPL, setValidantPL]               = useState(false);
  const [supprimantDraftPL, setSupprimantDraftPL] = useState(false);
  const [showReglPLPanel, setShowReglPLPanel]     = useState(false);
  const [reglPLSaisi, setReglPLSaisi]             = useState(null);
  const [activePLFac, setActivePLFac]             = useState(null); // { facture_id, ext_status }
  // Panel email facture
  const [factureEmailPanel, setFactureEmailPanel] = useState({ open: false, loading: false, destinataires: [] });
  const [envoyantFactureEmail, setEnvoyantFactureEmail] = useState(false);

  // Mode de périodicité de facturation (compatibilité : "total" legacy == "total_debut")
  const [modeFacturation, setModeFacturation] = useState(() => {
    const v = contrat.mode_periodicite_facturation || 'mensuel';
    return v === 'total' ? 'total_debut' : v;
  });
  const [savingMode, setSavingMode] = useState(false);

  const saveModeFacturation = async (newMode) => {
    setSavingMode(true);
    try {
      await api.updateContrat(contrat.contrat_id, { mode_periodicite_facturation: newMode });
      setModeFacturation(newMode);
      toast.success('Périodicité de facturation mise à jour');
      if (onContratUpdate) onContratUpdate();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    } finally { setSavingMode(false); }
  };

  // Correction des dates effectives par ligne matériel
  const [clLignes, setClLignes]         = useState([]);
  const [editingLigne, setEditingLigne] = useState(null); // ligne_id en cours d'édition
  const [ligneForm, setLigneForm]       = useState({});
  const [savingLigne, setSavingLigne]   = useState(false);

  const loadClLignes = useCallback(async () => {
    try {
      const r = await api.getClLignesDates(contrat.contrat_id);
      setClLignes(r.data || []);
    } catch { setClLignes([]); }
  }, [contrat.contrat_id]);

  useEffect(() => { loadClLignes(); }, [loadClLignes]);

  const startEditLigne = (ligne) => {
    setEditingLigne(ligne.ligne_id);
    setLigneForm({
      date_debut_effectif:  ligne.date_debut_effectif  || '',
      date_retour_effectif: ligne.date_retour_effectif || '',
    });
  };

  const saveLigneDates = async (ligneId) => {
    setSavingLigne(true);
    try {
      await api.patchClLigneDates(ligneId, {
        date_debut_effectif:  ligneForm.date_debut_effectif  || null,
        date_retour_effectif: ligneForm.date_retour_effectif || null,
      });
      await loadClLignes();
      setEditingLigne(null);
      toast.success('Dates mises à jour');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSavingLigne(false); }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.getFacturesLocation(contrat.contrat_id);
      setFactures(r.data || []);
    } catch { setFactures([]); }
    finally { setLoading(false); }
  }, [contrat.contrat_id]);

  useEffect(() => { load(); }, [load]);

  const supprimer = async (facture_id) => {
    if (!window.confirm('Supprimer cette facture brouillon ?')) return;
    try {
      await api.supprimerFactureLocation(facture_id);
      toast.success('Facture supprimée');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const validerPl = async (facture_id) => {
    if (!window.confirm('Valider cette facture dans Pennylane ?')) return;
    setPlAction(s => ({ ...s, [facture_id]: 'validating' }));
    try {
      await api.validerPlFactureLoc(facture_id);
      toast.success('Facture validée dans Pennylane');
      const facture = factures.find(f => f.facture_id === facture_id);
      if (facture && contrat.mode_reglement !== 'en_compte') {
        setReglPanel(facture_id);
      }
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Erreur lors de la validation';
      detail.startsWith('[DATE]')
        ? toast.warning(detail.replace('[DATE] ', ''), { duration: 10000 })
        : toast.error(detail);
    } finally { setPlAction(s => ({ ...s, [facture_id]: null })); }
  };

  const supprimerPl = async (facture_id) => {
    if (!window.confirm('Supprimer ce brouillon Pennylane ?')) return;
    setPlAction(s => ({ ...s, [facture_id]: 'deleting' }));
    try {
      await api.supprimerPlDraftLoc(facture_id);
      toast.success('Brouillon supprimé');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setPlAction(s => ({ ...s, [facture_id]: null })); }
  };

  const handleAcquittee = async () => {
    if (!activePLFac) return;
    setDownloadingAcquittee(true);
    try {
      const res = await api.getFactureAcquitteeBlob(activePLFac.facture_id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `facture-acquittee-${(activePLFac.ext_invoice_number || activePLFac.facture_id).replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'Erreur lors de la génération de la facture acquittée';
      try {
        const d = e?.response?.data;
        if (d instanceof Blob) { const t = await d.text(); msg = JSON.parse(t)?.detail || msg; }
        else if (d?.detail) msg = d.detail;
      } catch {}
      toast.error(msg);
    } finally {
      setDownloadingAcquittee(false);
    }
  };

  // ── Panel brouillon PL ────────────────────────────────────────────────────
  const openPlDraftPanelLoc = async (fac) => {
    setActivePLFac(fac);
    setPlDraftPanel({ loading: true, pdf_blob_url: null });
    const reglProm = api.getReglementsFactureLoc(fac.facture_id).then(r => r.data).catch(() => null);
    try {
      const [resp, reglData] = await Promise.all([
        api.getPlFactureLocPdfBlob(fac.facture_id),
        reglProm,
      ]);
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      setPlDraftPanel({ loading: false, pdf_blob_url: blobUrl });
      if (reglData?.reglements?.length > 0) setReglPLSaisi(reglData);
    } catch (e) {
      const detail = e.response?.data instanceof Blob ? null : (e.response?.data?.detail || null);
      if (detail) toast.error(detail);
      setPlDraftPanel({ loading: false, pdf_blob_url: null });
    }
  };

  const closePlDraftPanelLoc = () => {
    if (plDraftPanel?.pdf_blob_url) URL.revokeObjectURL(plDraftPanel.pdf_blob_url);
    setPlDraftPanel(null);
    setShowReglPLPanel(false);
    setReglPLSaisi(null);
    setActivePLFac(null);
  };

  // ── Création directe pour contrats comptant (évite AideFacturationLocation) ──
  const handleCreerFactureComptant = async () => {
    setCreatingFacture(true);
    try {
      const r = await api.creerFactureLocation(contrat.contrat_id, {});
      const num = r.data?.ext_invoice_number || r.data?.odoo_invoice_name || '';
      toast.success(num ? `Brouillon créé : ${num}` : 'Facture créée (brouillon)');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreatingFacture(false);
    }
  };

  // ── Email facture (Location) ─────────────────────────────────────────────────
  const handleEnvoyerFactureEmailLoc = async () => {
    setFactureEmailPanel({ open: true, loading: true, destinataires: [] });
    try {
      const r = await api.getDestinatairesFactureLocation(contrat.contrat_id);
      setFactureEmailPanel(p => ({ ...p, loading: false, destinataires: r.data?.destinataires || r.data || [], factureMeta: r.data?.facture || null }));
    } catch (e) {
      setFactureEmailPanel(p => ({ ...p, loading: false }));
      toast.error('Impossible de charger les destinataires');
    }
  };

  const handleConfirmFactureEmailLoc = async ({ emails, recipients, sujet, corps }) => {
    setEnvoyantFactureEmail(true);
    try {
      const r = await api.envoyerFactureEmailLocation(contrat.contrat_id, { to_emails: emails, recipients, sujet, corps });
      toast.success(r.data?.message || `Email(s) envoyé(s) à ${emails.length} destinataire(s)`);
      setFactureEmailPanel({ open: false, loading: false, destinataires: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setEnvoyantFactureEmail(false);
    }
  };

  const handleValiderPLLoc = async () => {
    if (!activePLFac?.facture_id) return;
    setValidantPL(true);
    try {
      await api.validerPlFactureLoc(activePLFac.facture_id);
      toast.success('Facture validée dans Pennylane');
      setActivePLFac(prev => prev ? { ...prev, ext_status: 'posted' } : prev);
      closePlDraftPanelLoc();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Erreur validation Pennylane';
      detail.startsWith('[DATE]')
        ? toast.warning(detail.replace('[DATE] ', ''), { duration: 10000 })
        : toast.error(detail);
    } finally { setValidantPL(false); }
  };

  const handleSupprimerBrouillonPLLoc = async () => {
    if (!activePLFac?.facture_id) return;
    if (!window.confirm('Supprimer ce brouillon dans Pennylane et dans la base ?')) return;
    setSupprimantDraftPL(true);
    try {
      await api.supprimerPlDraftLoc(activePLFac.facture_id);
      toast.success('Brouillon supprimé');
      closePlDraftPanelLoc();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression brouillon');
    } finally { setSupprimantDraftPL(false); }
  };

  const fmtDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 size={22} className="animate-spin" style={{ color: 'var(--primary-solid)' }} />
    </div>
  );

  return (
    <div className="space-y-4">

      {/* Périodicité de facturation */}
      <div className="rounded-xl border p-4" style={{ borderColor: 'var(--border)', background: 'white' }}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-0.5" style={{ color: 'var(--text-muted)' }}>
              Périodicité de facturation
            </p>
            <p className="text-xs" style={{ color: 'var(--text-disabled)' }}>
              Détermine comment ce contrat apparaît dans l'aide à la facturation
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            {savingMode && <span className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />}
            {/* Mensuel */}
            <button
              data-testid="mode-facturation-mensuel"
              disabled={savingMode}
              onClick={() => modeFacturation !== 'mensuel' && saveModeFacturation('mensuel')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all disabled:opacity-50"
              style={{
                background: modeFacturation === 'mensuel' ? 'var(--primary-solid)' : 'white',
                color: modeFacturation === 'mensuel' ? '#fff' : 'var(--text-muted)',
                borderColor: modeFacturation === 'mensuel' ? 'var(--primary-solid)' : 'var(--border)',
              }}>
              <span className="w-3 h-3 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                style={{ borderColor: modeFacturation === 'mensuel' ? '#fff' : 'var(--border)' }}>
                {modeFacturation === 'mensuel' && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
              </span>
              Terme mensuel
            </button>
            {/* Tout le contrat - Au début */}
            <button
              data-testid="mode-facturation-total-debut"
              disabled={savingMode}
              onClick={() => modeFacturation !== 'total_debut' && saveModeFacturation('total_debut')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all disabled:opacity-50"
              style={{
                background: modeFacturation === 'total_debut' ? 'var(--primary-solid)' : 'white',
                color: modeFacturation === 'total_debut' ? '#fff' : 'var(--text-muted)',
                borderColor: modeFacturation === 'total_debut' ? 'var(--primary-solid)' : 'var(--border)',
              }}>
              <span className="w-3 h-3 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                style={{ borderColor: modeFacturation === 'total_debut' ? '#fff' : 'var(--border)' }}>
                {modeFacturation === 'total_debut' && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
              </span>
              Tout le contrat - Au début
            </button>
            {/* Tout le contrat - A la fin */}
            <button
              data-testid="mode-facturation-total-fin"
              disabled={savingMode}
              onClick={() => modeFacturation !== 'total_fin' && saveModeFacturation('total_fin')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all disabled:opacity-50"
              style={{
                background: modeFacturation === 'total_fin' ? 'var(--primary-solid)' : 'white',
                color: modeFacturation === 'total_fin' ? '#fff' : 'var(--text-muted)',
                borderColor: modeFacturation === 'total_fin' ? 'var(--primary-solid)' : 'var(--border)',
              }}>
              <span className="w-3 h-3 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                style={{ borderColor: modeFacturation === 'total_fin' ? '#fff' : 'var(--border)' }}>
                {modeFacturation === 'total_fin' && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
              </span>
              Tout le contrat - A la fin
            </button>
          </div>
        </div>
        {modeFacturation === 'total_debut' && (
          <p className="mt-2 text-xs flex items-center gap-1.5" style={{ color: '#92400E' }}>
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#D97706' }} />
            Ce contrat sera proposé à la facturation dès le début du terme, pour toute la durée du contrat.
          </p>
        )}
        {modeFacturation === 'total_fin' && (
          <p className="mt-2 text-xs flex items-center gap-1.5" style={{ color: '#1D4ED8' }}>
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#3B82F6' }} />
            Ce contrat sera proposé à la facturation uniquement lors du retour du matériel, pour toute la durée effective.
          </p>
        )}
      </div>

      {/* Dates effectives par matériel — correction admin */}
      {clLignes.length > 0 && (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#BBF7D0' }}>
          <div className="flex items-center justify-between px-3 py-2"
            style={{ background: '#F0FDF4', borderBottom: '1px solid #BBF7D0' }}>
            <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: '#166534' }}>
              <Calendar size={13} />
              Dates effectives par matériel
            </div>
          </div>
          <div className="divide-y" style={{ borderColor: '#DCFCE7' }}>
            {clLignes.map(ligne => (
              <div key={ligne.ligne_id} className="px-3 py-2.5" style={{ background: 'white' }}>
                {editingLigne === ligne.ligne_id ? (
                  <div className="space-y-2">
                    <p className="text-xs font-bold" style={{ color: 'var(--text)' }}>{ligne.materiel_label}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs mb-1" style={{ color: '#166534' }}>Enlèvement effectif</label>
                        <input type="date" value={ligneForm.date_debut_effectif}
                          onChange={e => setLigneForm(f => ({ ...f, date_debut_effectif: e.target.value }))}
                          className="w-full px-2 py-1.5 rounded-lg border text-xs"
                          style={{ borderColor: '#BBF7D0' }} />
                      </div>
                      <div>
                        <label className="block text-xs mb-1" style={{ color: '#166534' }}>Retour effectif</label>
                        <input type="date" value={ligneForm.date_retour_effectif}
                          onChange={e => setLigneForm(f => ({ ...f, date_retour_effectif: e.target.value }))}
                          className="w-full px-2 py-1.5 rounded-lg border text-xs"
                          style={{ borderColor: '#BBF7D0' }} />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEditingLigne(null)}
                        className="px-2.5 py-1 rounded-lg text-xs" style={{ color: 'var(--text-muted)' }}>
                        Annuler
                      </button>
                      <button onClick={() => saveLigneDates(ligne.ligne_id)} disabled={savingLigne}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-white"
                        style={{ background: '#15803D', opacity: savingLigne ? 0.7 : 1 }}>
                        {savingLigne ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                        Enregistrer
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-bold flex-shrink-0" style={{ color: 'var(--text)' }}>
                        {ligne.materiel_label}
                      </span>
                      <span className="text-xs" style={{ color: '#166534' }}>
                        {ligne.date_debut_effectif
                          ? `${ligne.date_debut_effectif.slice(0,10)} → ${ligne.date_retour_effectif ? ligne.date_retour_effectif.slice(0,10) : 'En cours'}`
                          : <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Non renseigné</span>
                        }
                      </span>
                    </div>
                    {isAdmin && (
                      <button onClick={() => startEditLigne(ligne)}
                        className="p-1 rounded-md flex-shrink-0"
                        style={{ color: '#15803D' }}
                        data-testid={`btn-edit-ligne-dates-${ligne.ligne_id}`}>
                        <Edit2 size={11} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
          {factures.length} facture{factures.length !== 1 ? 's' : ''}
        </p>
        {contrat.contrat_parent_id ? (
          <div data-testid="badge-facture-via-parent" className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs"
            style={{ background: '#EDE9FE', color: '#7C3AED' }}>
            <Receipt size={13} />
            Facturé via le contrat parent
          </div>
        ) : (
          <button
            onClick={contrat.mode_reglement === 'comptant' ? handleCreerFactureComptant : () => navigate('/location/facturation', { state: { contrat_id: contrat.contrat_id } })}
            disabled={creatingFacture}
            data-testid="btn-creer-facture"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: 'var(--primary-gradient)' }}>
            {creatingFacture ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Nouvelle facture
          </button>
        )}
      </div>

      {/* Info : passage automatique à "Facturé" */}
      {contrat.statut !== 'annule' && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg text-xs"
          style={{ background: '#F5F3FF', color: '#7C3AED' }}>
          <Receipt size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            Le contrat passera automatiquement en statut <strong>Facturé</strong> dès que toutes les lignes
            seront couvertes par des factures validées.
          </span>
        </div>
      )}

      {/* Liste des factures */}
      {factures.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-10">
          <Euro size={28} style={{ color: 'var(--text-disabled)' }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucune facture pour ce contrat</p>
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>N° / Référence</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Date facture</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Date échéance</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Montant HT</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Montant TTC</th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {factures.map((f, idx) => {
                const isPl = f.ext_system === 'pennylane';
                const plStatus = f.ext_status;
                const stKey = isPl
                  ? (plStatus === 'posted' ? 'pl_valide' : plStatus === 'cancelled' ? 'pl_annule' : 'pl_brouillon')
                  : (f.odoo_statut || f.statut || 'brouillon');
                const st = STATUT_FAC_LABELS[stKey] || STATUT_FAC_LABELS.brouillon;
                return (
                  <tr key={f.facture_id} data-testid={`facture-row-${f.facture_id}`}
                    style={{ borderTop: idx > 0 ? '1px solid var(--border-light)' : 'none', background: 'white' }}>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-semibold" style={{ color: 'var(--text)' }}>
                          {f.ext_invoice_number || f.odoo_invoice_name || `…${f.facture_id.slice(-8)}`}
                        </span>
                        <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                          style={{ background: st.bg, color: st.color }}>
                          {st.label}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-xs tabular-nums" style={{ color: 'var(--text-secondary)' }}>
                      {fmtDateDMY(f.date_facture) || '—'}
                    </td>
                    <td className="px-3 py-2.5 text-xs tabular-nums" style={{ color: 'var(--text-secondary)' }}>
                      {fmtDateDMY(f.date_echeance) || '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs tabular-nums font-medium" style={{ color: 'var(--text)' }}>
                      {fmtEur(f.total_ht)}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs tabular-nums font-bold" style={{ color: 'var(--text)' }}>
                      {fmtEur(f.total_ttc)}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="inline-flex flex-col items-center gap-1.5">
                        {isPl && (
                          <button
                            onClick={() => openPlDraftPanelLoc(f)}
                            data-testid={`btn-voir-pdf-${f.facture_id}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors"
                            style={{ borderColor: '#BFDBFE', color: '#1D4ED8', background: '#EFF6FF' }}>
                            <FileText size={12} />Voir PDF
                          </button>
                        )}
                        {!isPl && f.pdf_path && (
                          <a href={f.pdf_path} target="_blank" rel="noreferrer"
                            data-testid={`btn-voir-pdf-${f.facture_id}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors"
                            style={{ borderColor: '#BFDBFE', color: '#1D4ED8', background: '#EFF6FF' }}>
                            <FileText size={12} />Voir PDF
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Panel règlements Pennylane */}
      {reglPanel && (() => {
        const facture = factures.find(f => f.facture_id === reglPanel);
        if (!facture) return null;
        return createPortal(
          <Dialog.Root open={true}>
            <Dialog.Content
              data-ignore-outside
              asChild
              onInteractOutside={e => e.preventDefault()}
              onOpenAutoFocus={e => e.preventDefault()}
              onCloseAutoFocus={e => e.preventDefault()}
            >
              <div style={{ position: 'fixed', inset: 0, zIndex: 10500, pointerEvents: 'none' }}>
                <ReglementPanel
                  devis={{
                    vente_id:      facture.facture_id,
                    numero:        facture.ext_invoice_number || facture.facture_id,
                    agence_id:     facture.agence_id,
                    entreprise_id: facture.entreprise_id,
                  }}
                  onClose={() => setReglPanel(null)}
                  onSaved={() => load()}
                  apiFns={{
                    get:    () => api.getReglementsFac(facture.facture_id),
                    create: (data) => api.createReglementFac(facture.facture_id, data),
                    delete: (regId) => api.deleteReglementFac(facture.facture_id, regId),
                  }}
                />
              </div>
            </Dialog.Content>
          </Dialog.Root>,
          document.body,
        );
      })()}

      {/* ── Panel brouillon PL (même pattern Ventes/AideFacturationLocation) ── */}
      {plDraftPanel !== null && createPortal(
        <div
          style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.45)', pointerEvents:'auto' }}
          onPointerDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) closePlDraftPanelLoc(); }}
        >
          <div
            style={{ position:'fixed', top:0, right:0, width:'min(1360px,92vw)', height:'100vh', background:'#ffffff', boxShadow:'-4px 0 32px rgba(0,0,0,0.18)', display:'flex', flexDirection:'column', pointerEvents:'auto' }}
            onPointerDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 24px', borderBottom:'1px solid #E5E7EB', flexShrink:0 }}>
              <div>
                <h2 style={{ fontSize:15, fontWeight:700, color:'#111827', margin:0 }}>Brouillon Pennylane</h2>
                <p style={{ fontSize:12, color:'#6B7280', margin:'2px 0 0 0' }}>Vérifiez la facture puis validez ou saisissez le règlement</p>
              </div>
              <button onClick={closePlDraftPanelLoc} style={{ background:'none', border:'none', cursor:'pointer', padding:4, color:'#6B7280' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ flex:1, overflow:'hidden', background:'#F9FAFB' }}>
              {plDraftPanel.loading ? (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', gap:12 }}>
                  <Loader2 size={32} className="animate-spin" style={{ color:'#0EA5E9' }} />
                  <span style={{ fontSize:13, color:'#6B7280' }}>Chargement du PDF…</span>
                </div>
              ) : plDraftPanel.pdf_blob_url ? (
                <iframe src={plDraftPanel.pdf_blob_url} title="Brouillon facture Pennylane" style={{ width:'100%', height:'100%', border:'none' }} />
              ) : (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', gap:12, padding:32 }}>
                  <FileText size={40} style={{ color:'#D1D5DB' }} />
                  <p style={{ fontSize:13, color:'#6B7280', textAlign:'center', margin:0 }}>PDF non disponible pour le moment.</p>
                </div>
              )}
            </div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 24px', borderTop:'1px solid #E5E7EB', gap:10, flexShrink:0 }}>
              {activePLFac?.ext_status !== 'posted' ? (
                <button data-testid="loc-pl-draft-supprimer" onClick={handleSupprimerBrouillonPLLoc} disabled={supprimantDraftPL || validantPL}
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:10, fontSize:13, fontWeight:600, background:'none', border:'1px solid #FECACA', color:'#DC2626', cursor:'pointer', opacity:(supprimantDraftPL||validantPL)?0.5:1 }}>
                  {supprimantDraftPL ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  Supprimer brouillon
                </button>
              ) : <span />}
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={closePlDraftPanelLoc} style={{ padding:'8px 16px', borderRadius:10, fontSize:13, fontWeight:600, background:'none', border:'1px solid #E5E7EB', color:'#374151', cursor:'pointer' }}>Fermer</button>
                <button data-testid="loc-pl-draft-reglement" onClick={() => setShowReglPLPanel(true)}
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:10, fontSize:13, fontWeight:600, background:reglPLSaisi?'rgba(34,197,94,0.1)':'none', border:reglPLSaisi?'1px solid rgba(34,197,94,0.5)':'1px solid #E5E7EB', color:reglPLSaisi?'#16a34a':'#374151', cursor:'pointer' }}>
                  <CreditCard size={13} />{reglPLSaisi ? (activePLFac?.ext_status==='posted'?'Voir le règlement':'Règlement saisi ✓') : 'Saisir le règlement'}
                </button>
                {/* Facture acquittée — visible si finalisée + entièrement payée */}
                {activePLFac?.ext_status === 'posted' &&
                 (activePLFac?.statut_paiement === 'payee' ||
                  (reglPLSaisi?.restant !== undefined && reglPLSaisi.restant <= 0 && reglPLSaisi.reglements?.length > 0)) && (
                  <button
                    data-testid="loc-pl-draft-acquittee"
                    onClick={handleAcquittee}
                    disabled={downloadingAcquittee}
                    style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:10, fontSize:13, fontWeight:600, background:'#F0FDF4', border:'1px solid #BBF7D0', color:'#15803D', cursor:'pointer', opacity: downloadingAcquittee ? 0.6 : 1 }}>
                    {downloadingAcquittee ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                    Facture acquittée
                  </button>
                )}
                {activePLFac?.ext_status !== 'posted' && (contrat?.mode_reglement !== 'comptant' || reglPLSaisi) && (
                  <button data-testid="loc-pl-draft-valider" onClick={handleValiderPLLoc} disabled={validantPL||supprimantDraftPL}
                    style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 20px', borderRadius:10, fontSize:13, fontWeight:700, background:'#0EA5E9', color:'#ffffff', border:'none', cursor:'pointer', opacity:(validantPL||supprimantDraftPL)?0.5:1 }}>
                    {validantPL ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                    Valider la facture
                  </button>
                )}
                {/* Envoyer la facture par email — visible si finalisée */}
                {activePLFac?.ext_status === 'posted' && (
                  <button
                    data-testid="loc-pl-draft-envoyer-email"
                    onClick={handleEnvoyerFactureEmailLoc}
                    disabled={envoyantFactureEmail}
                    style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:10, fontSize:13, fontWeight:600, background:'none', border:'1px solid #0EA5E9', color:'#0EA5E9', cursor:'pointer', opacity: envoyantFactureEmail ? 0.5 : 1 }}>
                    {envoyantFactureEmail ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                    Envoyer la facture par email
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Panel règlement PL (au-dessus du panel brouillon) */}
      {showReglPLPanel && activePLFac?.facture_id && createPortal(
        <Dialog.Root open={true}>
          <Dialog.Content
            data-ignore-outside
            asChild
            onInteractOutside={e => e.preventDefault()}
            onOpenAutoFocus={e => e.preventDefault()}
            onCloseAutoFocus={e => e.preventDefault()}
          >
            <div style={{ position:'fixed', inset:0, zIndex:10500, pointerEvents:'none' }}>
              <ReglementPanel
                devis={{ vente_id: activePLFac.facture_id }}
                onClose={() => setShowReglPLPanel(false)}
                onSaved={(d) => { setReglPLSaisi(d?.reglements?.length>0?d:null); setShowReglPLPanel(false); }}
                apiFns={{
                  get:    () => api.getReglementsFactureLoc(activePLFac.facture_id),
                  create: (d) => api.createReglementFactureLoc(activePLFac.facture_id, d),
                  delete: (rid) => api.deleteReglementFactureLoc(activePLFac.facture_id, rid),
                }}
              />
            </div>
          </Dialog.Content>
        </Dialog.Root>,
        document.body
      )}

      {/* Modale envoi facture par email */}
      <EmailComposeModal
        open={factureEmailPanel.open}
        onClose={() => setFactureEmailPanel(p => ({ ...p, open: false }))}
        onSend={handleConfirmFactureEmailLoc}
        destinataires={factureEmailPanel.destinataires}
        loadingDestinataires={factureEmailPanel.loading}
        typeCode="facture_location"
        templateVariables={{
          nom_client:       contrat?.client_nom || '',
          numero_facture:   factureEmailPanel.factureMeta?.ext_invoice_number || activePLFac?.ext_invoice_number || '',
          date_facture:     (factureEmailPanel.factureMeta?.date_facture || activePLFac?.date_facture)
            ? new Date(factureEmailPanel.factureMeta?.date_facture || activePLFac?.date_facture).toLocaleDateString('fr-FR') : '',
          total_ttc:        (factureEmailPanel.factureMeta?.montant_ttc ?? activePLFac?.total_ttc) != null
            ? `${Number(factureEmailPanel.factureMeta?.montant_ttc ?? activePLFac?.total_ttc).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} EUR` : '',
          nom_emetteur:     user ? (`${user.prenom || ''} ${user.nom || ''}`.trim() || user.name || user.email || '') : '',
          nom_destinataire: factureEmailPanel.destinataires?.[0]?.nom || '',
        }}
        agenceId={contrat?.agence_id || ''}
        entrepriseId={factureEmailPanel.factureMeta?.entreprise_id || null}
        titre="Envoyer la facture par email"
        documentRef={activePLFac?.ext_invoice_number ? `Facture ${activePLFac.ext_invoice_number}` : ''}
        sending={envoyantFactureEmail}
      />
    </div>
  );
}


// ─── SuspensionsTab ───────────────────────────────────────────────────────────

const MOTIFS_SUSPENSION = ['Intempéries', 'Panne matériel', 'Arrêt chantier'];
const MOTIF_ICONS = {
  'Intempéries': CloudSnow,
  'Panne matériel': Wrench,
  'Arrêt chantier': StopCircle,
};

function SuspensionsTab({ contrat, onSaved }) {
  const [addingFor, setAddingFor] = useState(null);
  const [motifsSuspension, setMotifsSuspension] = useState([]);
  const [form, setForm] = useState({ date_debut: '', date_fin: '', motif_id: '' });
  const [saving, setSaving] = useState(false);
  const [retourEffMap, setRetourEffMap] = useState({}); // materiel_id → date_retour_effectif

  const lignesMat = Array.isArray(contrat?.lignes_materiels) ? contrat.lignes_materiels : [];
  const suspensions = Array.isArray(contrat?.suspensions) ? contrat.suspensions : [];
  const activeLignes = lignesMat.filter(l => l.gamme_id);

  useEffect(() => {
    api.getMotifsSuspension()
      .then(r => {
        const items = r.data || [];
        setMotifsSuspension(items);
        if (!form.motif_id && items.length) setForm(f => ({ ...f, motif_id: items[0].motif_id }));
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  // Charger cl_lignes pour obtenir date_retour_effectif par matériel
  useEffect(() => {
    if (!contrat?.contrat_id) return;
    api.getClLignesDates(contrat.contrat_id)
      .then(r => {
        const map = {};
        (r.data || []).forEach(cl => {
          if (cl.materiel_id && cl.date_retour_effectif)
            map[cl.materiel_id] = cl.date_retour_effectif.slice(0, 10);
        });
        setRetourEffMap(map);
      })
      .catch(() => {});
  }, [contrat?.contrat_id]);

  const susForLigne = (idx) => suspensions.filter(s => s.ligne_index === idx);
  const totalJoursSuspendus = suspensions.reduce((acc, s) => acc + (s.nb_jours || 0), 0);

  const handleAdd = async (ligneIdx) => {
    if (!form.date_debut || !form.date_fin) { toast.error('Dates requises'); return; }
    if (form.date_fin < form.date_debut) { toast.error('La date de fin doit être ≥ date de début'); return; }
    if (!form.motif_id) { toast.error('Motif requis'); return; }
    setSaving(true);
    try {
      const res = await api.addSuspension(contrat.contrat_id, {
        ligne_index: ligneIdx,
        date_debut: form.date_debut,
        date_fin: form.date_fin,
        motif_id: form.motif_id,
      });
      toast.success('Suspension ajoutée');
      setAddingFor(null);
      setForm({ date_debut: '', date_fin: '', motif_id: motifsSuspension[0]?.motif_id || '' });
      onSaved(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (suspensionId) => {
    try {
      const res = await api.deleteSuspension(contrat.contrat_id, suspensionId);
      toast.success('Suspension supprimée');
      onSaved(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  if (activeLignes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-2 text-sm"
        style={{ color: 'var(--text-muted)' }}>
        <CloudSnow size={28} style={{ opacity: 0.4 }} />
        <p>Aucun matériel dans ce contrat</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Résumé global */}
      {totalJoursSuspendus > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium"
          style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>
          <CloudSnow size={14} />
          {totalJoursSuspendus} jour{totalJoursSuspendus > 1 ? 's' : ''} suspendu{totalJoursSuspendus > 1 ? 's' : ''} au total — montants recalculés
        </div>
      )}

      {activeLignes.map((ligne, rawIdx) => {
        const ligneIdx = lignesMat.indexOf(ligne);
        const sus = susForLigne(ligneIdx);
        const duree = parseInt(ligne.duree_jours || ligne.duree_calendrier || 0);
        const jSuspendus = sus.reduce((a, s) => a + (s.nb_jours || 0), 0);
        const jours = Math.max(0, duree - jSuspendus);

        const desig = [ligne.categorie_nom, ligne.gamme_nom, ligne.materiel_marque, ligne.materiel_modele]
          .filter(Boolean).join(' – ') || ligne.gamme_nom || '—';

        return (
          <div key={ligneIdx} className="rounded-xl border overflow-hidden"
            style={{ borderColor: 'var(--border)' }}>

            {/* En-tête ligne */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b"
              style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
              <div>
                <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{desig}</span>
                {jSuspendus > 0 && (
                  <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full font-bold"
                    style={{ background: '#FEF2F2', color: '#DC2626' }}>
                    −{jSuspendus} j
                  </span>
                )}
              </div>
              {jSuspendus > 0 && (
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {duree} j − {jSuspendus} j = <b>{jours} j facturé{jours > 1 ? 's' : ''}</b>
                </span>
              )}
            </div>

            {/* Liste suspensions */}
            <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
              {sus.length === 0 && addingFor !== ligneIdx && (
                <div className="px-4 py-3 text-xs italic" style={{ color: 'var(--text-muted)' }}>
                  Aucune suspension pour ce matériel
                </div>
              )}
              {sus.map(s => {
                const Icon = MOTIF_ICONS[s.motif] || CloudSnow;
                const period = s.date_debut === s.date_fin
                  ? s.date_debut
                  : `${s.date_debut} → ${s.date_fin}`;
                return (
                  <div key={s.suspension_id}
                    className="flex items-center justify-between px-4 py-2.5"
                    data-testid={`suspension-row-${s.suspension_id}`}>
                    <div className="flex items-center gap-2.5">
                      <Icon size={14} style={{ color: '#DC2626', flexShrink: 0 }} />
                      <div>
                        <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{s.motif}</span>
                        <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>{period}</span>
                      </div>
                      <span className="text-xs px-1.5 py-0.5 rounded-md font-semibold"
                        style={{ background: '#FEF2F2', color: '#DC2626' }}>
                        −{s.nb_jours} j
                      </span>
                    </div>
                    <button onClick={() => handleDelete(s.suspension_id)}
                      data-testid={`delete-suspension-${s.suspension_id}`}
                      className="p-1 rounded-lg hover:opacity-70 transition-opacity"
                      style={{ color: 'var(--text-muted)' }}>
                      <X size={14} />
                    </button>
                  </div>
                );
              })}

              {/* Formulaire ajout */}
              {addingFor === ligneIdx ? (
                <div className="px-4 py-3 space-y-3"
                  style={{ background: 'var(--bg-subtle)' }}>
                  {(() => {
                    const retourEff = ligne.materiel_id ? retourEffMap[ligne.materiel_id] : null;
                    const maxDate = (retourEff && retourEff > (ligne.date_fin?.slice(0,10) || ''))
                      ? retourEff
                      : (ligne.date_fin ? ligne.date_fin.slice(0,10) : undefined);
                    const maxLabel = maxDate;
                    return (
                    <>
                    <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                        Date début
                      </label>
                      <input type="date" value={form.date_debut}
                        min={ligne.date_debut ? ligne.date_debut.slice(0,10) : undefined}
                        max={maxDate}
                        onChange={e => setForm(f => ({ ...f, date_debut: e.target.value }))}
                        className="w-full px-2.5 py-1.5 rounded-lg border text-sm"
                        style={{ borderColor: 'var(--border)' }}
                        data-testid="suspension-date-debut" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                        Date fin
                      </label>
                      <input type="date" value={form.date_fin}
                        min={form.date_debut || (ligne.date_debut ? ligne.date_debut.slice(0,10) : undefined)}
                        max={maxDate}
                        onChange={e => setForm(f => ({ ...f, date_fin: e.target.value }))}
                        className="w-full px-2.5 py-1.5 rounded-lg border text-sm"
                        style={{ borderColor: 'var(--border)' }}
                        data-testid="suspension-date-fin" />
                    </div>
                  </div>
                  {ligne.date_debut && (
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      Plage autorisée&nbsp;: {ligne.date_debut?.slice(0,10)} → {maxLabel}
                      {retourEff && retourEff > (ligne.date_fin?.slice(0,10) || '') && (
                        <span style={{ color: '#D97706' }}> (retour effectif)</span>
                      )}
                    </p>
                  )}
                  </>
                  );
                  })()}
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                      Motif
                    </label>
                    {motifsSuspension.length === 0 ? (
                      <p className="text-xs" style={{ color: '#C2410C' }}>
                        Aucun motif configuré — ajoutez-en dans Paramètres → Location
                      </p>
                    ) : (
                      <select value={form.motif_id}
                        onChange={e => setForm(f => ({ ...f, motif_id: e.target.value }))}
                        className="w-full px-2.5 py-1.5 rounded-lg border text-sm"
                        style={{ borderColor: 'var(--border)' }}
                        data-testid="suspension-motif-select">
                        {motifsSuspension.map(m => (
                          <option key={m.motif_id} value={m.motif_id}>{m.libelle}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setAddingFor(null)}
                      className="px-3 py-1.5 text-xs rounded-lg border"
                      style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                      Annuler
                    </button>
                    <button onClick={() => handleAdd(ligneIdx)} disabled={saving}
                      data-testid="suspension-save-btn"
                      className="px-3 py-1.5 text-xs rounded-lg text-white font-semibold"
                      style={{ background: 'var(--primary-solid)' }}>
                      {saving ? 'Enregistrement…' : 'Enregistrer'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="px-4 py-2">
                  <button
                    onClick={() => {
                      setAddingFor(ligneIdx);
                      setForm({ date_debut: '', date_fin: '', motif_id: motifsSuspension[0]?.motif_id || '' });
                    }}
                    data-testid={`add-suspension-ligne-${ligneIdx}`}
                    className="flex items-center gap-1.5 text-xs font-semibold py-1 transition-opacity hover:opacity-70"
                    style={{ color: 'var(--primary-solid)' }}>
                    <Plus size={12} /> Ajouter une suspension
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ─── ContratPanel ─────────────────────────────────────────────────────────────

function ContratPanel({ contrat, gammes, articlesLocation, tvaRates = [], onClose, onSaved, prefillLigne, onOpenAvenant }) {
  const isNew = !contrat;
  const isAvenant = !!contrat?.contrat_parent_id;
  const navigate = useNavigate();
  const { activeAgences, societes } = useSociete();
  const initialClientId = useRef(contrat?.client_id || null);

  // ── Blocage client ─────────────────────────────────────────────────────────
  const [blocageClient, setBlocageClient] = useState(null);
  const [showBlocageModal, setShowBlocageModal] = useState(false);
  // ── Jours fériés de l'agence (pour calcul durée côté client) ─────────────
  const agenceIdForFeries = contrat?.agence_id || activeAgences?.[0]?.agence_id;
  const [feriesSet, setFeriesSet] = useState(new Set());
  useEffect(() => {
    if (!agenceIdForFeries) return;
    api.getJoursFeries(agenceIdForFeries)
      .then(r => setFeriesSet(new Set((r.data || []).map(j => j.date))))
      .catch(() => {});
  }, [agenceIdForFeries]);

  // ── TVA territoire-aware — résolue depuis le CP de l'agence du contrat ──
  const [localTvaRates, setLocalTvaRates] = useState(tvaRates);
  useEffect(() => {
    let cp = '';
    const agenceId = contrat?.agence_id;
    if (agenceId && societes) {
      outer: for (const soc of societes) {
        for (const ag of (soc.agences || [])) {
          if (ag.agence_id === agenceId) { cp = ag.code_postal || ''; break outer; }
        }
      }
    }
    if (!cp && activeAgences?.length) cp = activeAgences[0]?.code_postal || '';
    if (!cp && societes?.length)      cp = societes[0]?.agences?.[0]?.code_postal || '';
    api.resolveTva('vente', cp)
      .then(r => {
        const rates = (r.data?.taux_disponibles || []).map(t => ({
          taux: t.taux,
          tva_type_id: t.tva_type_id,
        }));
        const finalRates = rates.length > 0 ? rates : (tvaRates.length > 0 ? tvaRates : []);
        if (finalRates.length > 0) {
          setLocalTvaRates(finalRates);
          // Re-résoudre le taux TVA des lignes services avec article_id
          // (corrige les taux mal stockés, ex: 20% pour une agence DOM 971)
          setLignesSvc(prev => prev.map(l => {
            if (!l.article_id) return l;
            const art = articlesLocation?.find(a => a.article_id === l.article_id);
            if (!art?.tva_vente_type_id) return l;
            return { ...l, taux_tva: _resolveTauxTva(art.tva_vente_type_id, finalRates) };
          }));
        }
      })
      .catch(() => { if (tvaRates.length > 0) setLocalTvaRates(tvaRates); });
  }, [contrat?.agence_id, activeAgences, societes]);

  // Sync si le parent charge ses tvaRates APRÈS le mount de ContratPanel
  useEffect(() => {
    if (tvaRates.length > 0 && localTvaRates.length === 0) setLocalTvaRates(tvaRates);
  }, [tvaRates]); // eslint-disable-line

  const [activeTab, setActiveTab] = useState('informations');
  const { hasActivity } = useHasActivite('location', contrat?.contrat_id);

  const [client, setClient] = useState({
    client_id: contrat?.client_id || null,
    client_type: contrat?.client_type || null,
    client_nom: contrat?.client_nom || null,
  });

  // Vérifie le statut de blocage quand le client entreprise est défini
  useEffect(() => {
    if (client.client_id && client.client_type === 'entreprise') {
      api.getStatutBlocageClient(client.client_id)
        .then(r => setBlocageClient(r.data))
        .catch(() => setBlocageClient(null));
    } else {
      setBlocageClient(null);
    }
  }, [client.client_id, client.client_type]);

  const [interlocuteurs, setInterlocuteurs] = useState(() => {
    if (contrat?.interlocuteurs?.length) return contrat.interlocuteurs.map(i => ({ id: i.id, nom: i.nom, email: i.email || '' }));
    if (contrat?.contact_id) return [{ id: contrat.contact_id, nom: contrat.contact_nom || '', email: contrat.contact_email || '' }];
    return [];
  });
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  // Alias rétrocompat pour le premier interlocuteur (signataire, envoi mail…)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const contactPrimaire = interlocuteurs[0] || null;
  const [modeReglement, setModeReglement]           = useState(contrat?.mode_reglement || null);
  const [conditionsPaiementNom, setConditionsPaiementNom] = useState(contrat?.conditions_paiement_nom || null);
  const [validation, setValidation]                 = useState(contrat?.validation || { signature_recue: false, bc_recu: false, paiement_solde: false });
  const [lignesMat, setLignesMat]   = useState(() => {
    if (contrat?.lignes_materiels?.length) return contrat.lignes_materiels;
    // Pré-remplissage depuis le planning (clique sur cellule vide)
    const baseLigne = { ...EMPTY_LIGNE_MAT, ligne_id: `new-${Date.now()}` };
    if (prefillLigne) return [{ ...baseLigne, ...prefillLigne }];
    return [baseLigne];
  });
  const [lignesSvc, setLignesSvc]   = useState(contrat?.lignes_services || []);
  const [notes, setNotes]           = useState(contrat?.notes || '');
  const [refClient, setRefClient]   = useState(contrat?.ref_client || '');
  const [titre, setTitre]           = useState(contrat?.titre || '');
  const [adresseLivraison, setAdresseLivraison] = useState(contrat?.adresse_livraison || '');
  const [saving, setSaving]         = useState(false);
  const [creatingFactureComptant, setCreatingFactureComptant] = useState(false);
  const [prixModifies, setPrixModifies] = useState(false);
  const [showAvenant, setShowAvenant] = useState(false);
  const [avenantData, setAvenantData] = useState({ ligne_id: '', nouvelle_date_fin: '', motif: '' });
  const [childAvenants, setChildAvenants] = useState([]);
  const [isLastAvenant, setIsLastAvenant] = useState(false);
  useEffect(() => {
    if (!contrat?.contrat_id) return;
    if (isAvenant) {
      // Charger les frères pour déterminer si on est le dernier avenant actif
      if (!contrat.contrat_parent_id) return;
      api.getAvenants(contrat.contrat_parent_id)
        .then(r => {
          const siblings = (r.data?.items || []).filter(a => a.statut !== 'annule');
          const sorted = [...siblings].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          setIsLastAvenant(!sorted.length || sorted[0].contrat_id === contrat.contrat_id);
        })
        .catch(() => setIsLastAvenant(true));
    } else {
      api.getAvenants(contrat.contrat_id)
        .then(r => setChildAvenants(r.data?.items || []))
        .catch(() => {});
    }
  }, [contrat?.contrat_id]); // eslint-disable-line
  const [showEnvoiSignature, setShowEnvoiSignature] = useState(false);
  const [signaireNom, setSignaireNom] = useState(contrat?.contact_nom || '');

  // Parse défensif des champs JSONB qui peuvent arriver en string depuis la DB
  const avenantsList = Array.isArray(contrat?.avenants) ? contrat.avenants
    : (typeof contrat?.avenants === 'string' ? (() => { try { return JSON.parse(contrat.avenants); } catch { return []; } })() : []);
  const [signerMenuOpen, setSignerMenuOpen] = useState(false);
  const [showSignatureMail, setShowSignatureMail] = useState(false);
  const [showSignatureLien, setShowSignatureLien] = useState(false);
  // Modale "Signature reçue"
  const [showSignatureRecueModal, setShowSignatureRecueModal] = useState(false);
  const [signatureRecuePdf, setSignatureRecuePdf] = useState(null);
  const [savingSignatureRecue, setSavingSignatureRecue] = useState(false);
  const [sigEmail, setSigEmail]   = useState('');
  const [sigSujet, setSigSujet]   = useState('');
  const [sigCorps, setSigCorps]   = useState('');
  const signerMenuRef = useRef(null);
  // ── Dropdown interlocuteur ─────────────────────────────────────────────────
  const [contactDropOpen, setContactDropOpen] = useState(false);
  const contactDropRef = useRef(null);
  useEffect(() => {
    if (!contactDropOpen) return;
    const h = (e) => { if (contactDropRef.current && !contactDropRef.current.contains(e.target)) setContactDropOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [contactDropOpen]);
  // ── Envoyer le devis ──────────────────────────────────────────────────────
  const [envoyerDevisOpen, setEnvoyerDevisOpen]     = useState(false);
  const [mailDevisModal,   setMailDevisModal]        = useState(false);
  const [mailDevisForm,    setMailDevisForm]         = useState({ destinataire: '', sujet: '', corps: '' });
  const [sendingMailDevis, setSendingMailDevis]      = useState(false);
  const envoyerDevisRef = useRef(null);

  useEffect(() => {
    if (!envoyerDevisOpen) return;
    const h = (e) => { if (envoyerDevisRef.current && !envoyerDevisRef.current.contains(e.target)) setEnvoyerDevisOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [envoyerDevisOpen]);

  const handleImprimerDevis = async () => {
    setEnvoyerDevisOpen(false);
    try {
      const resp = await api.getContratPdf(contrat.contrat_id);
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${contrat.numero || contrat.contrat_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch { toast.error('Erreur lors de la génération du PDF'); }
  };

  const openMailDevisModal = () => {
    setEnvoyerDevisOpen(false);
    // Emails des interlocuteurs sélectionnés, séparés par virgule
    const destFromInterlocuteurs = interlocuteurs
      .map(i => i.email)
      .filter(Boolean)
      .join(', ');
    const dest = destFromInterlocuteurs || contrat?.contact_email || contrat?.client_email || '';
    const num  = contrat?.numero || '';
    setMailDevisForm({
      destinataire: dest,
      sujet: `Devis de location ${num} — ${contrat?.client_nom || ''}`.trim(),
      corps: `Bonjour,\n\nVeuillez trouver ci-joint votre devis de location n° ${num}.\n\nCordialement,`,
    });
    setMailDevisModal(true);
  };

  const handleSendMailDevis = async () => {
    if (!mailDevisForm.destinataire) { toast.error('Adresse email requise'); return; }
    setSendingMailDevis(true);
    try {
      const updated = await api.envoyerEmailContratDevis(contrat.contrat_id, mailDevisForm);
      toast.success(`Devis envoyé à ${mailDevisForm.destinataire}`);
      setMailDevisModal(false);
      onSaved?.(updated.data || updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi email');
    } finally { setSendingMailDevis(false); }
  };
  // ──────────────────────────────────────────────────────────────────────────
  const canvasRef = useRef(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const lastDrawPos = useRef(null);
  // Modale de signature unifiée
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [sigMode, setSigMode] = useState(null); // null | 'tablette' | 'ordinateur'
  // Documents contrat
  const [bls, setBls] = useState([]);
  const [docCount, setDocCount] = useState(0);

  // ── Actualiser les conditions paiement, le nom client et les contacts ──────
  const [refreshingConditions, setRefreshingConditions] = useState(false);
  const handleRefreshConditionsClient = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    if (!client.client_id || !contrat?.contrat_id) return;
    setRefreshingConditions(true);
    try {
      let newMode = modeReglement || 'comptant';
      let newCondNom = conditionsPaiementNom || null;
      let newClientNom = client.client_nom;

      if (client.client_type === 'personne') {
        newMode    = 'comptant';
        newCondNom = 'Paiement immédiat';
        // Relire le nom de la personne
        try {
          const pRes = await api.getPersonne(client.client_id);
          const p    = pRes.data;
          newClientNom = [p.prenom, p.nom].filter(Boolean).join(' ') || p.nom || client.client_nom;
        } catch { /* garder l'existant si erreur */ }
      } else {
        const [entRes, condRes, contactsRes] = await Promise.all([
          api.getEntreprise(client.client_id),
          api.getConditionsPaiement(),
          api.getPersonnesEntreprise(client.client_id),
        ]);
        const ent     = entRes.data;
        const mapping = condRes.data || [];
        const newList = Array.isArray(contactsRes.data?.items ?? contactsRes.data)
          ? (contactsRes.data?.items ?? contactsRes.data)
          : [];

        // Mettre à jour la liste de contacts dans le formulaire
        setContacts(newList);
        // Pré-remplir les emails des interlocuteurs déjà sélectionnés
        if (interlocuteurs.length > 0) {
          setInterlocuteurs(prev => prev.map(inter => {
            const found = newList.find(c => (c.personne_id || c.contact_id) === inter.id);
            return found ? { ...inter, email: found.email || found.email_pro || inter.email } : inter;
          }));
        }

        // Relire le nom de l'entreprise
        newClientNom = ent.nom || client.client_nom;

        // Relire les conditions de paiement pour l'agence RÉELLE du contrat
        // (et non le filtre "agence active" de la barre de navigation, qui peut différer)
        const cpAgence = ent?.conditions_paiement_par_agence?.[contrat?.agence_id] || null;
        if (cpAgence) {
          const mapped = mapping.find(c => c.id_odoo === cpAgence.id_odoo);
          // Priorité : mode_reglement stocké sur la fiche client > mapping parametres > défaut comptant
          newMode    = cpAgence.mode_reglement ?? mapped?.mode_reglement ?? 'comptant';
          newCondNom = cpAgence.nom_condition ?? null;
        }
      }

      // Construire la mise à jour (inclure client_nom si modifié)
      const updates = {
        mode_reglement:        newMode,
        conditions_paiement_nom: newCondNom,
      };
      if (newClientNom && newClientNom !== client.client_nom) {
        updates.client_nom = newClientNom;
      }

      await api.updateContrat(contrat.contrat_id, updates);

      setModeReglement(newMode);
      setConditionsPaiementNom(newCondNom);
      if (updates.client_nom) {
        setClient(prev => ({ ...prev, client_nom: newClientNom }));
      }

      const parts = [];
      if (updates.client_nom) parts.push(`client renommé "${newClientNom}"`);
      const condLabel = newMode === 'comptant' ? 'Comptant' : `En compte${newCondNom ? ` — ${newCondNom}` : ''}`;
      parts.push(`conditions : ${condLabel}`);
      if (client.client_type === 'entreprise') parts.push('contacts rechargés');
      if (!silent) toast.success(`Actualisé — ${parts.join(', ')}`);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || 'Erreur actualisation');
    } finally {
      setRefreshingConditions(false);
    }
  }, [client.client_id, client.client_type, client.client_nom, contrat?.contrat_id, activeAgences, interlocuteurs]);

  // ── Auto-actualisation silencieuse des données client à l'ouverture du contrat ──
  const autoRefreshedRef = useRef(false);
  useEffect(() => {
    if (isNew || !client.client_id || !contrat?.contrat_id || autoRefreshedRef.current) return;
    autoRefreshedRef.current = true;
    handleRefreshConditionsClient({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, client.client_id, contrat?.contrat_id]);

  // Fetch des contacts quand un client entreprise est sélectionné
  useEffect(() => {
    if (!client.client_id || client.client_type !== 'entreprise') {
      setContacts([]);
      setContactsLoading(false);
      // Pour un particulier : le client lui-même est l'interlocuteur
      if (client.client_type === 'personne' && client.client_id) {
        setInterlocuteurs([{ id: client.client_id, nom: client.client_nom || '', email: '' }]);
      }
      return;
    }
    setContactsLoading(true);
    api.getPersonnesEntreprise(client.client_id)
      .then(r => {
        const list = r.data?.items || r.data || [];
        setContacts(Array.isArray(list) ? list : []);
        // Pré-remplir les emails des interlocuteurs déjà sélectionnés (contrat existant)
        if (interlocuteurs.length > 0) {
          setInterlocuteurs(prev => prev.map(inter => {
            const found = list.find(c => (c.personne_id || c.contact_id) === inter.id);
            return found ? { ...inter, email: found.email || found.email_pro || inter.email } : inter;
          }));
        }
      })
      .catch((err) => {
        console.error('[ContratPanel] Erreur chargement contacts:', err?.response?.status, err?.message);
        setContacts([]);
      })
      .finally(() => setContactsLoading(false));
  }, [client.client_id, client.client_type]);

  // Charger les BLs du contrat (les documents sont gérés par DocumentsTab)
  const loadDocs = useCallback(async () => {
    if (isNew || !contrat?.contrat_id) return;
    const blsRes = await api.getBonsLivraisonContrat(contrat.contrat_id).catch(() => ({ data: { items: [] } }));
    setBls(blsRes.data?.items || []);
  }, [isNew, contrat?.contrat_id]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  // Hériter le mode_reglement depuis la fiche client (priorité : condition par agence > globale)
  // Ne s'exécute que si le client a été CHANGÉ par l'utilisateur (pas au chargement initial d'un contrat existant)
  useEffect(() => {
    if (client.client_id === initialClientId.current && !isNew) return; // contrat existant non modifié → garder valeur sauvegardée
    if (!client.client_id) { setModeReglement(null); setConditionsPaiementNom(null); return; }
    if (client.client_type === 'personne') {
      setModeReglement('comptant'); setConditionsPaiementNom('Paiement immédiat'); return;
    }
    // Fetch entreprise + mapping en parallèle pour éviter la race condition
    Promise.all([
      api.getEntreprise(client.client_id),
      api.getConditionsPaiement(),
    ]).then(([entRes, condRes]) => {
      const ent = entRes.data;
      if (!ent) return;
      const mapping = condRes.data || [];
      // Chercher la condition par agence active (1ère agence qui en a une)
      let cpAgence = null;
      for (const ag of (activeAgences || [])) {
        const cpa = ent.conditions_paiement_par_agence?.[ag.agence_id];
        if (cpa?.id_odoo || cpa?.nom_condition) { cpAgence = cpa; break; }
      }
      if (cpAgence) {
        const mapped = mapping.find(c => c.id_odoo === cpAgence.id_odoo);
        // Priorité : mode_reglement stocké sur la fiche client > mapping parametres > null
        setModeReglement(cpAgence.mode_reglement ?? mapped?.mode_reglement ?? null);
        setConditionsPaiementNom(cpAgence.nom_condition ?? null);
      } else {
        // Fallback : aucune condition par agence → paiement comptant par défaut
        setModeReglement('comptant');
        setConditionsPaiementNom(null);
      }
    }).catch(() => {});
  }, [client.client_id, client.client_type, activeAgences, isNew]);

  const totalLocation = lignesMat.reduce((s, l) => s + (l.montant || 0), 0);
  const totalServices = lignesSvc.reduce((s, l) => {
    const ht = (l.pu || 0) * (l.qte || 1) * (1 - (l.remise || 0) / 100);
    return s + ht;
  }, 0);
  const totalHT = totalLocation + totalServices;
  const totalTvaLocation = lignesMat.reduce((s, l) => s + (l.montant || 0) * (l.taux_tva || 0) / 100, 0);
  const totalTvaServices = lignesSvc.reduce((s, l) => {
    const ht = (l.pu || 0) * (l.qte || 1) * (1 - (l.remise || 0) / 100);
    return s + ht * (l.taux_tva || 0) / 100;
  }, 0);
  const totalTva = totalTvaLocation + totalTvaServices;
  const totalTtc = totalHT + totalTva;

  const updateLigneMat = (idx, updates) => {
    setLignesMat(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...updates };
      return next;
    });
    // Marquer les prix comme modifiés sur un contrat verrouillé (non devis/reserve)
    if (contrat && !['devis', 'reserve'].includes(contrat.statut) && 'prix_jour_applique' in updates) {
      setPrixModifies(true);
    }
  };
  const removeLigneMat = (idx) => setLignesMat(prev => prev.filter((_, i) => i !== idx));
  const addLigneMat = () => setLignesMat(prev => [...prev, { ...EMPTY_LIGNE_MAT, ligne_id: `new-${Date.now()}` }]);

  const addLigneSvc = useCallback((art) => {
    const autoRetour = contrat?.statut === 'retour';
    if (art?.type_ligne === 'texte' || art?.type_ligne === 'section') {
      setLignesSvc(prev => [...prev, {
        ligne_id: null, type_ligne: art.type_ligne,
        designation: art.designation || '', qte: 0, pu: 0, taux_tva: 0,
      }]);
    } else if (art?.article_id) {
      setLignesSvc(prev => [...prev, {
        ligne_id: null, type_ligne: 'prestation',
        article_id: art.article_id, reference: art.reference || '',
        designation: art.designation || '', qte: 1,
        pu: art.prix_vente || 0,
        taux_tva: _resolveTauxTva(art.tva_vente_type_id, localTvaRates),
        unite_symbole: art.unite_symbole || null,
        ...(autoRetour ? { facturer_au_retour: true } : {}),
      }]);
    }
  }, [localTvaRates, contrat?.statut]);
  const updateLigneSvc = (idx, field, val) => setLignesSvc(prev => { const n = [...prev]; n[idx] = { ...n[idx], [field]: val }; return n; });
  const removeLigneSvc = (idx) => setLignesSvc(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    const isConfirme    = contrat?.statut === 'confirme';
    const isInfosAdmin  = contrat && !['devis','reserve','confirme'].includes(contrat.statut) && contrat.statut !== 'annule';

    // Interlocuteur obligatoire uniquement pour les clients entreprise (pas sur clôturé)
    if (!isInfosAdmin && client.client_type !== 'personne' && (!interlocuteurs || interlocuteurs.length === 0)) {
      toast.error('Veuillez sélectionner un interlocuteur');
      return;
    }

    if (!isConfirme && !isInfosAdmin) {
      // Validations uniquement pour devis / reserve (lignes modifiables)
      if (!client.client_id) { toast.error('Client obligatoire'); return; }
      if (lignesMat.every(l => !l.gamme_id)) { toast.error('Au moins une gamme est requise'); return; }
      const lignesValides = lignesMat.filter(l => l.gamme_id);
      const sansDateDebut = lignesValides.filter(l => !l.date_debut);
      if (sansDateDebut.length > 0) {
        toast.error(`Date de début obligatoire pour : ${sansDateDebut.map(l => [l.categorie_nom, l.gamme_nom].filter(Boolean).join(' ')).join(', ')}`);
        return;
      }
      const sansDateFin = lignesValides.filter(l => !l.date_fin);
      if (sansDateFin.length > 0) {
        toast.error(`Date de fin obligatoire pour : ${sansDateFin.map(l => [l.categorie_nom, l.gamme_nom].filter(Boolean).join(' ')).join(', ')}`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = isInfosAdmin
        // Contrat clôturé : uniquement les 4 champs administratifs
        ? {
            titre:             titre.trim() || null,
            ref_client:        refClient.trim() || null,
            adresse_livraison: adresseLivraison.trim() || null,
            notes,
          }
        : isConfirme
        // Statut confirmé : champs méta autorisés (dont ref_client et notes)
        ? {
            contacts_ids:      interlocuteurs.map(i => i.id),
            titre:             titre.trim() || null,
            ref_client:        refClient.trim() || null,
            adresse_livraison: adresseLivraison.trim() || null,
            notes,
          }
        // Statut devis / reserve : payload complet
        : {
            client_id:         client.client_id,
            client_type:       client.client_type,
            client_nom:        client.client_nom,
            contacts_ids:      interlocuteurs.map(i => i.id),
            mode_reglement:    modeReglement,
            conditions_paiement_nom: conditionsPaiementNom,
            lignes_materiels:  lignesMat.filter(l => l.gamme_id).map(({ _gamme_changed, ...l }) => l),
            lignes_services:   lignesSvc,
            notes,
            ref_client:        refClient.trim() || null,
            titre:             titre.trim() || null,
            adresse_livraison: adresseLivraison.trim() || null,
          };

      if (isNew) {
        const res = await api.createContrat(payload);
        toast.success('Contrat créé');
        onSaved(res.data);
      } else {
        await api.updateContrat(contrat.contrat_id, payload);
        // Pour les contrats verrouillés (en_cours/retour/confirme), sauvegarder aussi les prix modifiés
        if (prixModifies && (isInfosAdmin || isConfirme)) {
          for (let seq = 0; seq < lignesMat.length; seq++) {
            const ligne = lignesMat[seq];
            if (ligne.prix_jour_applique != null) {
              await api.patchTarifLigne(contrat.contrat_id, seq, {
                prix_jour_applique: ligne.prix_jour_applique,
              });
            }
          }
          setPrixModifies(false);
        }
        toast.success('Contrat mis à jour');
        const refreshed = await api.getContrat(contrat.contrat_id);
        if (!isConfirme) {
          if (refreshed.data?.lignes_materiels?.length) setLignesMat(refreshed.data.lignes_materiels);
          if (refreshed.data?.lignes_services)          setLignesSvc(refreshed.data.lignes_services);
        }
        onSaved(refreshed.data);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
                : typeof detail === 'object' && detail ? JSON.stringify(detail)
                : e?.message || 'Erreur inconnue';
      console.error('[handleSave]', e?.response?.status, e?.response?.data, e);
      toast.error(msg, { duration: 15000 });
    } finally {
      setSaving(false);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const getDrawPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * (canvas.width / rect.width), y: (src.clientY - rect.top) * (canvas.height / rect.height) };
  };

  const handleSignerTabletteSubmit = async () => {
    if (!hasSignature) { toast.error('Veuillez apposer une signature'); return; }
    setSaving(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const res = await api.signerContratTerrain(contrat.contrat_id, {
        signataire_nom: signaireNom || contactPrimaire?.nom || client.client_nom || '',
        signature_image_b64: dataUrl,
      });
      toast.success('Contrat signé');
      setShowEnvoiSignature(false);
      setShowSignatureModal(false);
      clearCanvas();
      onSaved(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };


  const handleAnnulerSignature = async () => {
    setSaving(true);
    try {
      const res = await api.annulerSignatureTerrain(contrat.contrat_id);
      setValidation(res.data.validation);
      toast.success('Demande de signature annulée');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  // Fermer le menu Signer au clic extérieur
  useEffect(() => {
    if (!signerMenuOpen) return;
    const handler = (e) => { if (signerMenuRef.current && !signerMenuRef.current.contains(e.target)) setSignerMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [signerMenuOpen]);

  const handleSignatureMail = async () => {
    if (!sigEmail) { toast.error('Email requis'); return; }
    setSaving(true);
    try {
      const res = await api.envoyerSignatureMail(contrat.contrat_id, {
        signataire_nom: signaireNom || client.client_nom || '',
        signataire_email: sigEmail,
        signataire_contact_id: contactPrimaire?.id || null,
        sujet: sigSujet || undefined,
        corps: sigCorps || undefined,
      });
      toast.success('PDF envoyé par mail pour signature');
      setShowSignatureModal(false);
      setSigMode(null);
      onSaved(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur envoi mail'); }
    finally { setSaving(false); }
  };

  const handleSignatureLien = async () => {
    if (!sigEmail) { toast.error('Email requis'); return; }
    setSaving(true);
    try {
      const res = await api.envoyerSignatureLien(contrat.contrat_id, {
        signataire_nom: signaireNom || client.client_nom || '',
        signataire_email: sigEmail,
        signataire_contact_id: contactPrimaire?.id || null,
        sujet: sigSujet || undefined,
        corps: sigCorps || undefined,
      });
      toast.success('Lien de signature envoyé par mail');
      setShowSignatureModal(false);
      setSigMode(null);
      onSaved(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur envoi lien'); }
    finally { setSaving(false); }
  };

  const handleSignatureRecue = async () => {
    setSavingSignatureRecue(true);
    try {
      const fd = new FormData();
      if (signatureRecuePdf) fd.append('pdf', signatureRecuePdf);
      const res = await api.signatureRecue(contrat.contrat_id, fd);
      onSaved(res);
      setShowSignatureRecueModal(false);
      setSignatureRecuePdf(null);
      toast.success('Contrat confirmé — signature reçue');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la confirmation');
    } finally {
      setSavingSignatureRecue(false);
    }
  };

  const handleAnnulerLienSignature = async () => {    setSaving(true);
    try {
      const res = await api.annulerLienSignatureLocation(contrat.contrat_id);
      toast.success('Lien de signature annulé');
      onSaved(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur annulation lien'); }
    finally { setSaving(false); }
  };

  // ── Création directe de facture pour contrats comptant (stepper) ─────────
  const handleCreerFactureComptantStepper = async () => {
    setCreatingFactureComptant(true);
    // Pour un avenant, facturer via le contrat parent (le backend rejette les avenants directs)
    const targetId = isAvenant ? (contrat.contrat_parent_id || contrat.contrat_id) : contrat.contrat_id;
    try {
      const r = await api.creerFactureLocation(targetId, {});
      const num = r.data?.ext_invoice_number || r.data?.odoo_invoice_name || '';
      toast.success(num ? `Brouillon créé : ${num}` : 'Facture créée (brouillon)');
      const refreshed = await api.getContrat(contrat.contrat_id);
      if (onSaved) onSaved(refreshed.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreatingFactureComptant(false);
    }
  };

  const handleTransition = async (to) => {
    if (to === 'en_cours') {
      // Livraison : sauvegarder les lignes (affectations matériels) puis créer les BL
      setSaving(true);
      try {
        // 1. Persister les lignes avec les matériels sélectionnés (endpoint permissif pour statut confirme)
        await api.updateLignesMateriels(contrat.contrat_id, lignesMat.filter(l => l.gamme_id));
        // 2. Déclencher la livraison
        const res = await api.declencherLivraison(contrat.contrat_id);
        const count = res.data?.count || 0;
        const msg = count > 0
          ? `${count} bon${count > 1 ? 's' : ''} de livraison créé${count > 1 ? 's' : ''} — contrat en cours`
          : 'Contrat passé en cours';
        toast.success(msg);
        const refreshed = await api.getContrat(contrat.contrat_id);
        onSaved(refreshed.data);
        loadDocs();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Erreur lors de la création des bons de livraison');
      } finally { setSaving(false); }
      return;
    }
    if (to === 'retour') {
      // Retour global : création des bons de retour pour tous les matériels sans BR existant
      setSaving(true);
      try {
        const res = await api.declencherRetour(contrat.contrat_id);
        const count = res.data?.count || 0;
        toast.success(`${count} bon${count > 1 ? 's' : ''} de retour créé${count > 1 ? 's' : ''} — en attente de signature terrain`);
        const refreshed = await api.getContrat(contrat.contrat_id);
        onSaved(refreshed.data);
        loadDocs();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Erreur lors de la création des bons de retour');
      } finally { setSaving(false); }
      return;
    }
    setSaving(true);
    try {
      await api.transitionContrat(contrat.contrat_id, { statut: to });
      toast.success(`Statut mis à jour : ${statutInfo(to).label}`);
      const refreshed = await api.getContrat(contrat.contrat_id);
      onSaved(refreshed.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  // ── Retour partiel : créer un BL retour pour un matériel spécifique ───────────
  const materielsAvecBR = useMemo(() => {
    const map = {};
    bls.filter(b => b.type === 'retour' && b.statut !== 'annule' && b.materiel_id)
       .forEach(b => { map[b.materiel_id] = b; });
    return map;
  }, [bls]);

  // ── Livraison partielle : tracker les BL livraison par matériel ───────────────
  const materielsAvecBL = useMemo(() => {
    const map = {};
    bls.filter(b => b.type === 'livraison' && b.statut !== 'annule' && b.materiel_id)
       .forEach(b => { map[b.materiel_id] = b; });
    return map;
  }, [bls]);

  const handleRetourMateriel = async (ligne) => {
    setSaving(true);
    try {
      await api.declencherRetour(contrat.contrat_id, ligne.materiel_id);
      toast.success(`Bon de retour créé pour ${ligne.materiel_nom || ligne.gamme_nom || 'ce matériel'} — en attente de signature terrain`);
      const refreshed = await api.getContrat(contrat.contrat_id);
      onSaved(refreshed.data);
      loadDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création du bon de retour');
    } finally { setSaving(false); }
  };

  // ── Livraison partielle : créer un BL livraison pour un matériel spécifique ──
  const handleLivraisonMateriel = async (ligne) => {
    setSaving(true);
    try {
      await api.declencherLivraison(contrat.contrat_id, ligne.materiel_id);
      toast.success(`Bon de livraison créé pour ${ligne.materiel_nom || ligne.gamme_nom || 'ce matériel'} — en attente de signature terrain`);
      const refreshed = await api.getContrat(contrat.contrat_id);
      onSaved(refreshed.data);
      loadDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création du bon de livraison');
    } finally { setSaving(false); }
  };

  const handleAvenant = async () => {
    if (!avenantData.ligne_id || !avenantData.nouvelle_date_fin) {
      toast.error('Ligne et nouvelle date obligatoires');
      return;
    }
    const ligne = contrat.lignes_materiels.find(l => l.ligne_id === avenantData.ligne_id);
    if (!ligne) return;
    // ancienne_date_fin = date_fin effective (max parmi les avenants non annulés pour cette gamme)
    const effectiveDateFin = childAvenants
      .filter(av => av.statut !== 'annule')
      .flatMap(av => av.lignes_materiels || [])
      .filter(al => al.gamme_id === ligne.gamme_id && (!ligne.materiel_id || !al.materiel_id || al.materiel_id === ligne.materiel_id))
      .reduce((max, al) => (al.date_fin > max ? al.date_fin : max), ligne.date_fin);
    setSaving(true);
    try {
      const res = await api.createAvenant(contrat.contrat_id, {
        type: 'prolongation',
        ligne_id: avenantData.ligne_id,
        ancienne_date_fin: effectiveDateFin,
        nouvelle_date_fin: avenantData.nouvelle_date_fin,
        motif: avenantData.motif,
      });
      // Ajouter l'avenant à la liste locale
      setChildAvenants(prev => [...prev, res.data]);
      toast.success('Avenant créé');
      // Rafraîchir le contrat parent (nouvelles dates + totaux)
      const refreshed = await api.getContrat(contrat.contrat_id);
      onSaved(refreshed.data);
      setShowAvenant(false);
      setAvenantData({ ligne_id: '', nouvelle_date_fin: '', motif: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const canEditLignes      = !contrat || ['devis', 'reserve'].includes(contrat.statut);
  const canEditMeta        = !contrat || ['devis', 'reserve', 'confirme'].includes(contrat.statut);
  const canEditServices    = !contrat || (contrat.statut !== 'annule');
  const canEditPrix        = !contrat || contrat.statut !== 'annule';
  // Champs administratifs modifiables même sur contrat clôturé (sauf annulé)
  const canEditInfosAdmin  = !contrat || contrat.statut !== 'annule';

  // Sauvegarde rapide des prestations seules (contrats verrouillés : en_cours, retour, confirme)
  const [savingSvc, setSavingSvc] = useState(false);
  const handleSaveSvcOnly = async () => {
    setSavingSvc(true);
    try {
      await api.updateContrat(contrat.contrat_id, { lignes_services: lignesSvc });
      toast.success('Prestations enregistrées');
      const refreshed = await api.getContrat(contrat.contrat_id);
      if (refreshed.data?.lignes_services) setLignesSvc(refreshed.data.lignes_services);
      onSaved(refreshed.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingSvc(false);
    }
  };

  const rawTransitions = contrat ? (TRANSITIONS[contrat.statut] || []) : [];
  const activeAvenants = childAvenants.filter(a => a.statut !== 'annule');
  // hasActiveAvenants: utilise nb_avenants_actifs (dispo dès le 1er rendu) en plus du chargement async
  const hasActiveAvenants = activeAvenants.length > 0 || (contrat?.nb_avenants_actifs ?? 0) > 0;
  let transitions = rawTransitions.filter(t => {
    if (isAvenant) {
      // Pour un avenant : pas de réservation ni de livraison (machine déjà chez le client)
      if (t.to === 'reserve')  return false;
      if (t.to === 'en_cours') return false;
      // Retour autorisé uniquement sur le dernier avenant
      if (t.to === 'retour' && !isLastAvenant) return false;
      return true;
    }
    // Pour le contrat parent : bloquer le retour si des avenants actifs existent
    if (t.to === 'retour' && hasActiveAvenants) return false;
    return true;
  });
  // Dernier avenant en 'confirme' : ajouter la transition retour (machine déjà livrée via parent)
  if (isLastAvenant && isAvenant && contrat?.statut === 'confirme') {
    transitions = [...transitions, { to: 'retour', label: 'Retour (check-in)', color: '#15803D' }];
  }

  // Client comptant : débloqué dès qu'au moins une facture existe
  const facturePayee = (contrat?.nb_factures ?? 0) > 0;
  const lignesSansMatch = lignesMat.filter(l => l.gamme_id && !l.materiel_id);
  const livraisonBloquee = contrat?.statut === 'confirme' && lignesSansMatch.length > 0;

  // Prérequis avant livraison
  const handleToggleFlag = async (flag, value) => {
    if (!contrat) return;
    try {
      const res = await api.patchValidationContrat(contrat.contrat_id, { [flag]: value });
      setValidation(res.data?.validation || {});
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  return (
    <>
    <SidePanel
      open
      onOpenChange={(v) => { if (!v) onClose(); }}
      size="60vw"
      title={isNew ? 'Nouveau contrat' : contrat.numero}
      copyValue={!isNew ? contrat.numero : undefined}
      headerCenter={
        (blocageClient?.bloque || blocageClient?.jours_retard > 0) ? (
          <button
            data-testid={blocageClient?.bloque ? 'badge-blocage-location' : 'badge-retard-location'}
            onClick={() => setShowBlocageModal(true)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer transition-all hover:opacity-80"
            style={blocageClient?.bloque
              ? { background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }
              : { background: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA' }}>
            <AlertTriangle size={11} />
            {blocageClient?.bloque ? 'Bloqué paiement' : 'Retard paiement'}
          </button>
        ) : null
      }
      headerExtra={!isNew ? (
        <div className="flex items-center justify-between flex-1 min-w-0 gap-4">
          {/* Date — près du numéro */}
          <div className="flex items-center gap-1 flex-shrink-0" style={{ color: 'var(--text-disabled)', fontSize: '0.68rem' }}>
            <Calendar size={10} />
            <span>Créé le {fmtDate(contrat.created_at)}</span>
          </div>
          {/* Badge avenant (contrat parent) */}
          {isAvenant && onOpenAvenant && (
            <button
              data-testid="btn-retour-contrat-parent"
              onClick={() => onOpenAvenant({ contrat_id: contrat.contrat_parent_id })}
              className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 transition-opacity hover:opacity-80"
              style={{ background: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA' }}>
              ← Contrat parent
            </button>
          )}
          {/* Badge facturation + Stepper + Reminder — droite */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Badge facturation */}
            {(() => {
              const hasUnbilledAvenants = activeAvenants.some(a => !(a.nb_factures > 0));
              const avenAntsLoaded = childAvenants.length > 0 || (contrat?.nb_avenants_actifs ?? 0) === 0;
              if (contrat.statut === 'retour' && contrat.nb_factures > 0 && !hasUnbilledAvenants && avenAntsLoaded) return (
                <span data-testid="badge-facturation-complet"
                  className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                  style={{ background: '#DCFCE7', color: '#15803D' }}>
                  Facturé ✓
                </span>
              );
              if (contrat.statut === 'retour') return (
                <span data-testid="badge-facturation-a-facturer"
                  className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                  style={{ background: '#FEF3C7', color: '#B45309' }}>
                  À facturer
                </span>
              );
              if (contrat.statut === 'en_cours' && contrat.derniere_periode_fin) return (
                <span data-testid="badge-facturation-partiel"
                  className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                  style={{ background: '#FEF3C7', color: '#B45309' }}>
                  {new Date(contrat.derniere_periode_fin + 'T12:00:00').toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                </span>
              );
              return null;
            })()}

            {/* Stepper — droite */}
          <div className="flex items-center flex-shrink-0" style={{ height: 26 }}>
            {(() => {
              const STEPS = [
                { value: 'devis',    label: 'Devis' },
                { value: 'reserve',  label: 'Réservé' },
                { value: 'confirme', label: 'Confirmé' },
                { value: 'en_cours', label: 'Enlevé' },
                { value: 'retour',   label: 'Retourné' },
              ];
              const arrow = 10;
              const curIdx = STEPS.findIndex(s => s.value === contrat.statut);
              const isAnnule = contrat.statut === 'annule';
              return (
                <>
                  {STEPS.map((step, i) => {
                    const isLast = i === STEPS.length - 1 && !isAnnule;
                    const active = !isAnnule && i === curIdx;
                    const past   = !isAnnule && i < curIdx;
                    const clipPath = isLast
                      ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
                      : `polygon(0 0, calc(100% - ${arrow}px) 0, 100% 50%, calc(100% - ${arrow}px) 100%, 0 100%)`;
                    return (
                      <div key={step.value}
                        style={{
                          clipPath,
                          height: '26px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: `0 ${isLast ? 10 : 18}px 0 12px`,
                          marginLeft: i > 0 ? -arrow : 0,
                          position: 'relative',
                          zIndex: STEPS.length - i,
                          background: isAnnule ? '#E5E7EB' : active ? 'var(--primary-gradient)' : past ? '#C9D6E3' : '#E8EDF2',
                          color: isAnnule ? '#9CA3AF' : active ? '#fff' : past ? '#3D5775' : '#8A9BB0',
                          fontWeight: active ? '700' : '500',
                          fontSize: '0.72rem',
                          whiteSpace: 'nowrap',
                          letterSpacing: '0.01em',
                          textDecoration: isAnnule ? 'line-through' : 'none',
                        }}>
                        {step.label}
                      </div>
                    );
                  })}
                  {/* Branche terminale Annulé */}
                  {isAnnule && (
                    <div style={{
                      clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)',
                      height: '26px',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 10px 0 14px',
                      marginLeft: -arrow,
                      position: 'relative',
                      zIndex: 0,
                      background: '#DC2626',
                      color: '#fff',
                      fontWeight: '700',
                      fontSize: '0.72rem',
                      whiteSpace: 'nowrap',
                      letterSpacing: '0.01em',
                    }}>
                      Annulé
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          </div>{/* fin wrapper badge + stepper */}
        </div>
      ) : undefined}
    >
      <div className="flex flex-col gap-5 p-1 pb-8">

        {/* Transitions workflow */}
        {transitions.length > 0 && (
          <div className={isAvenant && contrat?.statut === 'devis' ? "flex flex-wrap gap-2 items-center" : "space-y-2"}>
            {/* Bouton Signer (dropdown) + Annuler — visible à 'reserve' pour les contrats normaux, et à 'devis' pour les avenants (qui sautent l'étape réservation) */}
            {(contrat?.statut === 'reserve' || (isAvenant && contrat?.statut === 'devis')) && (
              <div className="flex items-center gap-3 flex-wrap">
                {blocageClient?.bloque ? (
                  <button data-testid="badge-blocage-location-action"
                    onClick={() => setShowBlocageModal(true)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer"
                    style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>
                    <AlertTriangle size={12} /> Bloqué paiement — voir détails
                  </button>
                ) : (
                <div className="relative" ref={signerMenuRef}>
                <button
                  data-testid="btn-signer"
                  onClick={() => setSignerMenuOpen(o => !o)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
                  style={{ background: '#D97706' }}>
                  <PenTool size={14} /> Confirmer <ChevronDown size={13} />
                </button>
                {signerMenuOpen && (
                  <div className="absolute top-full left-0 mt-1 w-72 rounded-xl shadow-xl border z-50 overflow-hidden"
                    style={{ borderColor: 'var(--border)', background: '#fff' }}>
                    <button
                      data-testid="signer-tablette"
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50 border-b"
                      style={{ borderColor: 'var(--border-light)' }}
                      onClick={() => { setSignerMenuOpen(false); setSignaireNom(contactPrimaire?.nom || client.client_nom || ''); setSigMode('tablette'); setShowSignatureModal(true); }}>
                      <Tablet size={16} style={{ color: '#7C3AED' }} />
                      <div>
                        <div className="font-semibold" style={{ color: 'var(--text)' }}>Signature tablette</div>
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Signer au doigt sur tablette tactile</div>
                      </div>
                    </button>
                    <button
                      data-testid="signer-ordinateur"
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50 border-b"
                      style={{ borderColor: 'var(--border-light)' }}
                      onClick={() => { setSignerMenuOpen(false); setSignaireNom(contactPrimaire?.nom || client.client_nom || ''); setSigMode('ordinateur'); setShowSignatureModal(true); }}>
                      <Monitor size={16} style={{ color: '#059669' }} />
                      <div>
                        <div className="font-semibold" style={{ color: 'var(--text)' }}>Signature ordinateur</div>
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Signer à la souris sur cet écran</div>
                      </div>
                    </button>
                    <button
                      data-testid="signer-mail"
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50 border-b"
                      style={{ borderColor: 'var(--border-light)' }}
                      onClick={() => { setSignerMenuOpen(false); setSignaireNom(contactPrimaire?.nom || client.client_nom || ''); setSigEmail(contactPrimaire?.email || interlocuteurs[0]?.email || ''); setSigSujet(`${contrat?.numero} — Signature requise`); setSigCorps(`Bonjour,\n\nVeuillez trouver ci-joint le contrat ${contrat?.numero} à signer.\n\nMerci de nous retourner le document signé par email dès que possible.\n\nCordialement,`); setSigMode('mail'); setShowSignatureModal(true); }}>
                      <Mail size={16} style={{ color: '#1D4ED8' }} />
                      <div>
                        <div className="font-semibold" style={{ color: 'var(--text)' }}>PDF par mail</div>
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Envoyer le PDF à signer par retour</div>
                      </div>
                    </button>
                    <button
                      data-testid="signer-lien"
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-gray-50"
                      onClick={() => { setSignerMenuOpen(false); setSignaireNom(contactPrimaire?.nom || client.client_nom || ''); setSigEmail(contactPrimaire?.email || interlocuteurs[0]?.email || ''); setSigSujet(`${contrat?.numero} — Signez en ligne`); setSigCorps(`Bonjour,\n\nVotre contrat ${contrat?.numero} est prêt à être signé.\n\nCliquez sur le bouton ci-dessous pour accéder à la page de signature en ligne.\n\nCordialement,`); setSigMode('lien'); setShowSignatureModal(true); }}>
                      <LinkIcon size={16} style={{ color: '#C2410C' }} />
                      <div>
                        <div className="font-semibold" style={{ color: 'var(--text)' }}>Lien de signature en ligne</div>
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Envoyer un lien par mail pour signer en ligne</div>
                      </div>
                    </button>
                  </div>
                )}
              </div>
                )}
              {/* Badge lien en attente + bouton annulation */}
              {contrat?.validation?.signature_lien_envoye && !contrat?.validation?.signature_recue && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl flex-shrink-0"
                  style={{ background: '#FFF7ED', border: '1px solid #FED7AA' }}>
                  <Clock size={13} style={{ color: '#D97706' }} />
                  <span
                    data-testid="badge-lien-en-attente"
                    className="text-xs font-semibold whitespace-nowrap" style={{ color: '#92400E' }}>
                    Lien envoyé — en attente de signature
                  </span>
                  <div className="flex items-center gap-1.5 ml-2">
                    <button
                      data-testid="copier-lien-signature"
                      onClick={() => {
                        const token = contrat?.validation?.signature_web_token;
                        if (token) { navigator.clipboard.writeText(`${window.location.origin}/signer/${token}`); toast.success('Lien copié'); }
                      }}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                      style={{ background: '#D97706', color: '#fff' }}>
                      <Copy size={12} /> Copier
                    </button>
                    <button
                      data-testid="renvoyer-lien-signature"
                      onClick={() => { setSignaireNom(contactPrimaire?.nom || client.client_nom || ''); setSigEmail(contactPrimaire?.email || interlocuteurs[0]?.email || ''); setSigSujet(`${contrat?.numero} — Signez en ligne`); setSigCorps(`Bonjour,\n\nVotre contrat ${contrat?.numero} est prêt à être signé.\n\nCliquez sur le bouton ci-dessous pour accéder à la page de signature en ligne.\n\nCordialement,`); setSigMode('lien'); setShowSignatureModal(true); }}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                      style={{ background: '#FDE68A', color: '#92400E', border: '1px solid #FCD34D' }}>
                      <Send size={12} /> Renvoyer
                    </button>
                    <button
                      data-testid="annuler-lien-signature"
                      onClick={handleAnnulerLienSignature}
                      disabled={saving}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold disabled:opacity-40 transition-all hover:opacity-80"
                      style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>
                      <X size={12} /> Annuler le lien
                    </button>
                  </div>
                </div>
              )}
              {/* Badge + bouton "Signature reçue" — visible quand le PDF a été envoyé par mail */}
              {contrat?.validation?.signature_methode === 'mail' && !contrat?.validation?.signature_recue && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    data-testid="badge-mail-en-attente"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold"
                    style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
                    <Mail size={12} /> PDF envoyé — en attente du retour signé
                  </span>
                  <button
                    data-testid="btn-signature-recue"
                    onClick={() => setShowSignatureRecueModal(true)}
                    disabled={saving}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-40 transition-all hover:opacity-90"
                    style={{ background: '#059669' }}>
                    <Check size={12} /> Signature reçue
                  </button>
                </div>
              )}
              {/* Bouton Annuler inline — uniquement pour le statut 'reserve' (pour les avenants en devis, il est dans le bloc standard en dessous) */}
              {contrat?.statut === 'reserve' && transitions.filter(t => t.to === 'annule').map(t => (
                <button key={t.to} onClick={() => handleTransition(t.to)} disabled={saving}
                  data-testid="transition-annule"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-40"
                  style={{ background: t.color }}>
                  <ArrowRight size={14} /> {t.label}
                </button>
              ))}
            </div>
            )}

            {/* Message bloquant si matériel manquant */}
            {livraisonBloquee && (
              <div className="rounded-xl border px-3 py-2 text-xs font-medium flex items-center gap-2"
                style={{ borderColor: '#FDE68A', background: '#FFFBEB', color: '#92400E' }}>
                <span>⚠</span>
                Assignez un matériel à chaque ligne avant de livrer :
                <span className="font-bold">
                  {lignesSansMatch.map(l => [l.categorie_nom, l.gamme_nom].filter(Boolean).join(' ')).join(', ')}
                </span>
              </div>
            )}

            {/* Boutons de transition standard (hors confirme) */}
            <div className="flex flex-wrap gap-2 items-center">
              {/* Bouton "Envoyer le devis" — statut devis uniquement */}
              {contrat?.statut === 'devis' && !isNew && (
                <>
                  <div className="relative" ref={envoyerDevisRef}>
                    <button
                      data-testid="btn-envoyer-devis"
                      onClick={() => setEnvoyerDevisOpen(o => !o)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                      style={{ background: '#1D4ED8' }}>
                      <Send size={13} />
                      Envoyer le devis
                      <ChevronDown size={13} style={{ marginLeft: 2 }} />
                    </button>
                    {envoyerDevisOpen && (
                      <div className="absolute left-0 top-full mt-1.5 rounded-xl border bg-white shadow-xl z-50 overflow-hidden"
                        style={{ borderColor: 'var(--border)', minWidth: 210 }}>
                        <button
                          data-testid="btn-envoyer-devis-email"
                          onClick={openMailDevisModal}
                          className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors border-b"
                          style={{ borderColor: '#F3F4F6' }}>
                          <Mail size={14} style={{ color: '#1D4ED8' }} />
                          <div>
                            <div className="text-sm font-medium" style={{ color: '#111827' }}>Envoyer par email</div>
                            <div className="text-xs" style={{ color: '#6B7280' }}>PDF en pièce jointe</div>
                          </div>
                        </button>
                        <button
                          data-testid="btn-imprimer-devis"
                          onClick={handleImprimerDevis}
                          className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                          <Printer size={14} style={{ color: '#6B7280' }} />
                          <div>
                            <div className="text-sm font-medium" style={{ color: '#111827' }}>Imprimer / PDF</div>
                            <div className="text-xs" style={{ color: '#6B7280' }}>Ouvre le PDF dans un nouvel onglet</div>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                  {contrat?.validation?.devis_email_envoye && (
                    <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                      style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
                      <Mail size={11} />
                      Envoyé à {contrat.validation.devis_email_destinataire || '—'}
                    </span>
                  )}
                </>
              )}
              {contrat?.statut !== 'reserve' && transitions.map(t => {
                // Cas spécial : client comptant en statut "confirme" → guider vers facturation avant livraison (contrat parent)
                if (t.to === 'en_cours' && modeReglement === 'comptant') {
                  return (
                    <div key="comptant-gate" className="w-full rounded-xl border p-3 space-y-2"
                      style={{ borderColor: '#FDE68A', background: '#FFFBEB' }}>
                      {!facturePayee && (
                        <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: '#92400E' }}>
                          <CreditCard size={13} />
                          Paiement comptant — facturer avant la livraison
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {!facturePayee && (
                          <button
                            data-testid="btn-aller-facturation"
                            onClick={handleCreerFactureComptantStepper}
                            disabled={creatingFactureComptant}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-60"
                            style={{ background: '#D97706' }}>
                            {creatingFactureComptant ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} Créer la facture
                          </button>
                        )}
                        <button
                          onClick={handleRefreshConditionsClient}
                          disabled={refreshingConditions}
                          data-testid="btn-refresh-conditions-client"
                          title="Re-lire les conditions de paiement depuis la fiche client"
                          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold disabled:opacity-40 transition-all"
                          style={{ background: 'var(--bg-secondary)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                          {refreshingConditions
                            ? <span className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                            : <RefreshCw size={13} />}
                          Actualiser
                        </button>
                        <button
                          key={t.to}
                          onClick={() => handleTransition(t.to)}
                          disabled={saving || !facturePayee}
                          title={!facturePayee ? 'Créer une facture pour débloquer la livraison' : ''}
                          data-testid={`transition-${t.to}`}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-40 transition-all"
                          style={{ background: t.color }}>
                          <ArrowRight size={14} /> {t.label}
                        </button>
                      </div>
                    </div>
                  );
                }
                // Cas spécial : avenant comptant → guider vers facturation avant clôture (retour)
                if (t.to === 'retour' && isAvenant && modeReglement === 'comptant') {
                  return (
                    <div key="avenant-comptant-gate" className="w-full rounded-xl border p-3 space-y-2"
                      style={{ borderColor: '#FDE68A', background: '#FFFBEB' }}>
                      {!facturePayee && (
                        <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: '#92400E' }}>
                          <CreditCard size={13} />
                          Paiement comptant — facturer immédiatement après signature de l'avenant
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {!facturePayee && (
                          <button
                            data-testid="btn-aller-facturation-avenant"
                            onClick={handleCreerFactureComptantStepper}
                            disabled={creatingFactureComptant}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-60"
                            style={{ background: '#D97706' }}>
                            {creatingFactureComptant ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} Créer la facture
                          </button>
                        )}
                        <button
                          key={t.to}
                          onClick={() => handleTransition(t.to)}
                          disabled={saving || !facturePayee}
                          title={!facturePayee ? 'Créer une facture pour débloquer la clôture' : ''}
                          data-testid={`transition-${t.to}`}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-40 transition-all"
                          style={{ background: t.color }}>
                          <ArrowRight size={14} /> {t.label}
                        </button>
                      </div>
                    </div>
                  );
                }
                return (
                  <button key={t.to} onClick={() => handleTransition(t.to)}
                    disabled={saving || (t.to === 'en_cours' && livraisonBloquee)}
                    title={t.to === 'en_cours' && livraisonBloquee
                      ? `Matériel requis pour : ${lignesSansMatch.map(l => [l.categorie_nom, l.gamme_nom].filter(Boolean).join(' ')).join(', ')}`
                      : undefined}
                    data-testid={`transition-${t.to}`}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-40 transition-all"
                    style={{ background: t.color }}>
                    <ArrowRight size={14} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}


        {/* ── Grid 2 colonnes : ligne 1 = Client+Titre / ligne 2 = Interlocuteur+Adresse ── */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">

          {/* [Ligne 1, Col gauche] Client + badge mode règlement */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold flex-shrink-0 whitespace-nowrap" style={{ color: 'var(--text-muted)', width: 100 }}>Client *</span>
              <div className="flex-1 min-w-0">
                <ClientSearchField
                  value={client}
                  onChange={c => { setClient(prev => ({ ...prev, ...c })); setInterlocuteurs([]); }}
                  onOpen={c => navigate('/annuaire', { state: { openClientId: c.client_id, openClientType: c.client_type } })}
                />
              </div>
            </div>
            {modeReglement && (
              <div className="flex items-center gap-2 flex-wrap" style={{ paddingLeft: 108 }}>
                <span className="inline-flex text-xs px-2 py-1 rounded-lg font-semibold items-center gap-1 self-start"
                  style={modeReglement === 'comptant'
                    ? { background: '#F0FDF4', color: '#15803D', border: '1px solid #86EFAC' }
                    : { background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #93C5FD' }}>
                  <CreditCard size={10} />
                  {modeReglement === 'comptant' ? 'Comptant' : 'En compte'}
                  {conditionsPaiementNom && <span className="font-normal opacity-70 ml-1">— {conditionsPaiementNom}</span>}
                </span>
              </div>
            )}
          </div>

          {/* [Ligne 1, Col droite] Titre + Vos références */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold flex-shrink-0 whitespace-nowrap" style={{ color: 'var(--text-muted)', width: 118 }}>Titre</span>
              <input
                type="text"
                value={titre}
                onChange={e => setTitre(e.target.value)}
                placeholder="Titre du contrat (optionnel)"
                className="flex-1 min-w-0 px-2.5 py-2 rounded-xl border text-sm"
                style={{ borderColor: 'var(--border)' }}
                data-testid="contrat-titre"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold flex-shrink-0 whitespace-nowrap" style={{ color: 'var(--text-muted)', width: 118 }}>Vos références</span>
              <input
                type="text"
                value={refClient}
                onChange={e => setRefClient(e.target.value)}
                placeholder={"Ex\u00a0: BC-2026-0042"}
                className="flex-1 min-w-0 px-2.5 py-2 rounded-xl border text-sm"
                style={{ borderColor: 'var(--border)' }}
                data-testid="ref-client-input"
              />
            </div>
          </div>

          {/* [Ligne 2, Col gauche] Interlocuteur(s) — aligné avec Adresse de livraison */}
          <div>
            {client.client_id && client.client_type === 'entreprise' && (
              <div className="flex items-start gap-2">
                <span className="text-xs font-semibold flex-shrink-0 whitespace-nowrap pt-2" style={{ color: 'var(--text-muted)', width: 100 }}>Interlocuteur(s)</span>
                <div className="relative flex-1 min-w-0" ref={contactDropRef} data-testid="contact-select-wrapper">
                  <button
                    type="button"
                    data-testid="contact-select"
                    onClick={() => { if (contacts.length > 0 && !contactsLoading) setContactDropOpen(o => !o); }}
                    disabled={contactsLoading}
                    className="w-full flex items-center justify-between px-2.5 py-2 rounded-xl border text-sm text-left min-h-[38px]"
                    style={{
                      borderColor: contactDropOpen ? '#6366F1' : 'var(--border)',
                      background: 'var(--bg)',
                      outline: contactDropOpen ? '2px solid #EEF2FF' : 'none',
                      cursor: contacts.length > 0 && !contactsLoading ? 'pointer' : 'default',
                      opacity: contactsLoading ? 0.6 : 1,
                      gap: 6,
                    }}>
                    <span className="flex flex-wrap gap-1 flex-1">
                      {contactsLoading ? (
                        <span className="flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
                          <span className="w-3 h-3 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin inline-block" />
                          Chargement…
                        </span>
                      ) : interlocuteurs.length === 0 ? (
                        <span style={{ color: 'var(--text-muted)' }}>
                          {contacts.length === 0 ? '— Sélectionnez un contact —' : '— Aucun interlocuteur sélectionné —'}
                        </span>
                      ) : interlocuteurs.map(inter => (
                        <span key={inter.id}
                          className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ background: '#EEF2FF', color: '#4338CA' }}>
                          {inter.nom}
                          <span
                            role="button"
                            data-testid={`contact-chip-remove-${inter.id}`}
                            onClick={e => { e.stopPropagation(); setInterlocuteurs(prev => prev.filter(i => i.id !== inter.id)); }}
                            className="cursor-pointer hover:opacity-70 ml-0.5"
                            style={{ fontSize: 14, lineHeight: 1, color: '#6366F1' }}>×</span>
                        </span>
                      ))}
                    </span>
                    <ChevronDown size={14} style={{ flexShrink: 0, color: 'var(--text-muted)', transform: contactDropOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                  </button>
                  {contactDropOpen && contacts.length > 0 && (
                    <div className="absolute left-0 top-full mt-1 w-full rounded-xl border shadow-lg z-50 overflow-hidden"
                      style={{ borderColor: 'var(--border)', background: '#fff', maxHeight: 240, overflowY: 'auto' }}>
                      {contacts.map(c => {
                        const id = c.personne_id || c.contact_id;
                        const nom = `${c.prenom || ''} ${c.nom || ''}`.trim() || c.nom_complet || String(id);
                        const isSelected = interlocuteurs.some(i => i.id === id);
                        return (
                          <button
                            key={id}
                            type="button"
                            data-testid={`contact-option-${id}`}
                            onClick={() => {
                              if (isSelected) {
                                setInterlocuteurs(prev => prev.filter(i => i.id !== id));
                              } else {
                                setInterlocuteurs(prev => [...prev, { id, nom, email: c.email || '' }]);
                              }
                            }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-indigo-50 transition-colors"
                            style={{ background: isSelected ? '#EEF2FF' : 'transparent' }}>
                            <span className="flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center"
                              style={{ borderColor: isSelected ? '#6366F1' : 'var(--border)', background: isSelected ? '#6366F1' : 'transparent' }}>
                              {isSelected && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                            </span>
                            <span className="flex-1 truncate" style={{ color: 'var(--text)' }}>{nom}</span>
                            {c.poste && <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{c.poste}</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* [Ligne 2, Col droite] Adresse de livraison — aligné avec Interlocuteur */}
          <div className="flex items-start gap-2">
            <span className="text-xs font-semibold flex-shrink-0 whitespace-nowrap pt-2" style={{ color: 'var(--text-muted)', width: 118 }}>Adresse de livraison</span>
            <textarea
              value={adresseLivraison}
              onChange={e => setAdresseLivraison(e.target.value)}
              placeholder="Adresse de livraison (optionnel)"
              rows={3}
              className="flex-1 min-w-0 px-2.5 py-2 rounded-xl border text-sm resize-none"
              style={{ borderColor: 'var(--border)' }}
              data-testid="contrat-adresse-livraison"
            />
          </div>

        </div>

        {/* ─── Onglets ─── */}
        <div className="border-t" style={{ borderColor: 'var(--border)', marginTop: '4px' }}>
          <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
            {[
              { id: 'informations', label: 'Informations', badge: null },
              { id: 'suspensions',  label: 'Suspensions',  badge: (() => { const s = Array.isArray(contrat?.suspensions) ? contrat.suspensions : []; return s.length > 0 ? s.length : null; })() },
              { id: 'notes',        label: 'Notes',        badge: notes.trim().length > 0 ? '•' : null },
              { id: 'documents',    label: 'Documents',    badge: (docCount + bls.length) > 0 ? (docCount + bls.length) : null },
              { id: 'facturation',  label: 'Facturation',  badge: null },
              { id: 'discussions',  label: 'Activités',    badge: null },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
                style={{
                  borderBottomColor: activeTab === tab.id ? 'var(--primary-solid)' : 'transparent',
                  color: activeTab === tab.id ? 'var(--primary-solid)' : 'var(--text-muted)',
                  background: 'transparent',
                  marginBottom: '-1px',
                }}>
                {tab.label}
                {tab.id === 'discussions' && hasActivity
                  ? <ActivityPulse />
                  : tab.badge !== null && (
                    <span className="px-1.5 py-0.5 rounded-full text-xs font-bold"
                      style={{
                        background: activeTab === tab.id ? 'var(--primary-solid)' : 'var(--text-disabled)',
                        color: 'white',
                        fontSize: '0.65rem',
                        minWidth: '16px',
                        textAlign: 'center',
                      }}>
                      {tab.badge}
                    </span>
                  )
                }
              </button>
            ))}
          </div>

          <div className="pt-5 space-y-5">

            {/* ─── Informations ─── */}
            {activeTab === 'informations' && (
              <div className="space-y-5">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Matériels loués</label>
                    {canEditLignes && (
                      <button
                        onClick={addLigneMat}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold"
                        style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                        <Plus size={11} /> Ajouter
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    {lignesMat.map((l, i) => {
                      const blRetour = materielsAvecBR[l.materiel_id];
                      const peutRetourner = contrat?.statut === 'en_cours' && l.materiel_id && !blRetour && !hasActiveAvenants;
                      const dejaRetourne = blRetour?.statut === 'signe';
                      const retourPending = blRetour && blRetour.statut !== 'signe';

                      const blLivraison = materielsAvecBL[l.materiel_id];
                      // isAvenant : la livraison a déjà eu lieu sur le contrat parent → jamais de nouveau BL
                      const peutLivrer = !isAvenant && contrat?.statut === 'confirme' && l.materiel_id && !blLivraison;
                      const dejaLivre = blLivraison?.statut === 'signe';
                      const livraisonPending = blLivraison && blLivraison.statut !== 'signe';

                      return (
                        <div key={l.ligne_id || i} className="relative">
                          <LigneMaterielForm ligne={l} gammes={gammes}
                            feriesSet={feriesSet}
                            agenceId={agenceIdForFeries}
                            canEdit={canEditLignes}
                            canEditMateriel={canEditMeta}
                            canEditPrix={canEditPrix}
                            excludedMaterielIds={lignesMat.filter((_, j) => j !== i).map(o => o.materiel_id).filter(Boolean)}
                            onUpdate={(upd) => updateLigneMat(i, upd)} onRemove={() => removeLigneMat(i)} />

                          {/* Statuts / boutons livraison partielle (contrat confirme) */}
                          {dejaLivre && (
                            <div className="mt-1 ml-1 flex items-center gap-1 text-xs font-medium"
                              style={{ color: '#1D4ED8' }}>
                              <Truck size={12} />
                              Livré {blLivraison.date_effective ? `le ${fmtDate(blLivraison.date_effective)}` : ''}
                            </div>
                          )}
                          {livraisonPending && (
                            <div className="mt-1 ml-1 flex items-center gap-1 text-xs font-medium"
                              style={{ color: '#7C3AED' }}>
                              <Truck size={12} />
                              BL en attente de signature
                            </div>
                          )}
                          {peutLivrer && (
                            <button
                              data-testid={`livraison-materiel-${l.materiel_id}`}
                              onClick={() => handleLivraisonMateriel(l)}
                              disabled={saving}
                              className="mt-1 ml-1 flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold disabled:opacity-50"
                              style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
                              <Truck size={11} /> Créer BL livraison
                            </button>
                          )}

                          {/* Statuts / boutons retour partiel (contrat en_cours) */}
                          {dejaRetourne && (
                            <div className="mt-1 ml-1 flex items-center gap-1 text-xs font-medium"
                              style={{ color: '#16A34A' }}>
                              <CheckCircle2 size={12} />
                              Retourné {blRetour.date_effective ? `le ${fmtDate(blRetour.date_effective)}${blRetour.date_effective && blRetour.date_effective.includes('T') ? ' à ' + new Date(blRetour.date_effective).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'}) : ''}` : ''}
                            </div>
                          )}
                          {retourPending && (
                            <div className="mt-1 ml-1 flex items-center gap-1 text-xs font-medium"
                              style={{ color: '#C2410C' }}>
                              <LogOut size={12} />
                              BR en attente de signature
                            </div>
                          )}
                          {peutRetourner && (
                            <button
                              data-testid={`retour-materiel-${l.materiel_id}`}
                              onClick={() => handleRetourMateriel(l)}
                              disabled={saving}
                              className="mt-1 ml-1 flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold disabled:opacity-50"
                              style={{ background: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA' }}>
                              <LogOut size={11} /> Créer BR retour
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                    Services additionnels
                  </label>

                  {/* Renonciation à recours — au-dessus du tableau */}
                  {lignesSvc.filter(l => l.is_assurance).map((l, i) => (
                    <div key={l.ligne_id || `assurance-${i}`}
                      className="flex items-center justify-between mt-2 px-3 py-2 rounded-lg"
                      style={{ background: '#FFF7ED', border: '1px solid #FED7AA' }}>
                      <div>
                        <span className="text-sm font-semibold" style={{ color: '#C2410C' }}>{l.designation}</span>
                        <span className="text-xs ml-2" style={{ color: '#9A3412' }}>8% du total location</span>
                      </div>
                      <span className="text-sm font-bold tabular-nums" style={{ color: '#C2410C' }}>
                        {(l.pu || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                      </span>
                    </div>
                  ))}

                  {/* Tableau services */}
                  <table className="w-full mt-2" style={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                    <colgroup>
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '8%'  }} />
                      <col style={{ width: '26%' }} />
                      <col style={{ width: '7%'  }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '7%'  }} />
                      <col style={{ width: '11%' }} />
                      <col style={{ width: '5%'  }} />
                    </colgroup>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        {['Actions','Réf','Désignation','Qté','PU Net','TVA %','Total HT',''].map((h, j) => (
                          <th key={j} className={`px-2 py-1.5 text-xs font-semibold ${j >= 3 && j <= 6 ? 'text-center' : j === 7 ? 'text-center' : 'text-left'}`}
                            style={{ color: 'var(--text-muted)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {lignesSvc.filter(l => !l.is_assurance).map((l, _) => {
                        const realIdx = lignesSvc.indexOf(l);
                        return (
                          <LigneSvcRow key={l.ligne_id || realIdx} l={l} i={realIdx}
                            tvaRates={localTvaRates} canEdit={canEditServices}
                            onUpdate={(f, v) => updateLigneSvc(realIdx, f, v)}
                            onRemove={() => removeLigneSvc(realIdx)}
                            onToggleFlag={async (ligneId, field, value) => {
                              try {
                                await api.patchLigneServiceFlags(contrat.contrat_id, ligneId, { [field]: value });
                                updateLigneSvc(realIdx, field, value);
                              } catch { toast.error('Erreur lors de la mise à jour'); }
                            }}
                          />
                        );
                      })}
                      {canEditServices && <SvcAddRow onSelect={addLigneSvc} />}
                    </tbody>
                  </table>
                </div>
                {/* Bouton "Enregistrer les prestations" — visible si services éditables mais formulaire principal verrouillé */}
                {canEditServices && !canEditLignes && !isNew && (
                  <div className="flex justify-end pt-1">
                    <button
                      data-testid="save-svc-only-btn"
                      onClick={handleSaveSvcOnly}
                      disabled={savingSvc}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                      style={{ background: 'var(--primary-gradient)' }}>
                      {savingSvc
                        ? <><span className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#fff', borderTopColor: 'transparent' }} /> Enregistrement...</>
                        : 'Enregistrer les prestations'}
                    </button>
                  </div>
                )}
                {(totalLocation > 0 || totalServices > 0) && (
                  <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Récapitulatif</th>
                            <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Montant HT</th>
                            <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Montant TVA</th>
                            <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Montant TTC</th>
                          </tr>
                        </thead>
                        <tbody>
                          {totalLocation > 0 && (
                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                              <td className="px-4 py-2" style={{ color: 'var(--text-muted)' }}>Location</td>
                              <td className="px-4 py-2 text-right font-medium" style={{ color: 'var(--text)' }}>{fmtEur(totalLocation)}</td>
                              <td className="px-4 py-2 text-right font-medium" style={{ color: 'var(--text)' }}>{fmtEur(totalTvaLocation)}</td>
                              <td className="px-4 py-2 text-right font-medium" style={{ color: 'var(--text)' }}>{fmtEur(totalLocation + totalTvaLocation)}</td>
                            </tr>
                          )}
                          {totalServices > 0 && (
                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                              <td className="px-4 py-2" style={{ color: 'var(--text-muted)' }}>Services</td>
                              <td className="px-4 py-2 text-right font-medium" style={{ color: 'var(--text)' }}>{fmtEur(totalServices)}</td>
                              <td className="px-4 py-2 text-right font-medium" style={{ color: 'var(--text)' }}>{fmtEur(totalTvaServices)}</td>
                              <td className="px-4 py-2 text-right font-medium" style={{ color: 'var(--text)' }}>{fmtEur(totalServices + totalTvaServices)}</td>
                            </tr>
                          )}
                          <tr style={{ background: 'var(--bg-subtle)' }}>
                            <td className="px-4 py-2.5 font-bold" style={{ color: 'var(--text)' }}>Total</td>
                            <td className="px-4 py-2.5 text-right font-bold" style={{ color: 'var(--primary-solid)' }}>{fmtEur(totalHT)}</td>
                            <td className="px-4 py-2.5 text-right font-bold" style={{ color: 'var(--primary-solid)' }}>{fmtEur(totalTva)}</td>
                            <td className="px-4 py-2.5 text-right font-bold" style={{ color: 'var(--primary-solid)' }}>{fmtEur(totalTtc)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {!isNew && contrat.statut !== 'annule' && !isAvenant && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                        Avenants ({childAvenants.length})
                      </label>
                      <button onClick={() => setShowAvenant(v => !v)} data-testid="add-avenant-btn"
                        className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold"
                        style={{ background: '#FFF7ED', color: '#C2410C' }}>
                        <RotateCcw size={11} /> Prolongation
                      </button>
                    </div>
                    {showAvenant && (
                      <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: '#FED7AA' }}>
                        <select value={avenantData.ligne_id} onChange={e => setAvenantData(p => ({ ...p, ligne_id: e.target.value, nouvelle_date_fin: '' }))}
                          className="w-full px-2.5 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }}>
                          <option value="">Ligne matériel...</option>
                          {contrat.lignes_materiels?.map(l => {
                            const effDateFin = childAvenants
                              .filter(av => av.statut !== 'annule')
                              .flatMap(av => av.lignes_materiels || [])
                              .filter(al => al.gamme_id === l.gamme_id && (!l.materiel_id || !al.materiel_id || al.materiel_id === l.materiel_id))
                              .reduce((max, al) => (al.date_fin > max ? al.date_fin : max), l.date_fin);
                            return (
                              <option key={l.ligne_id} value={l.ligne_id}>
                                {l.gamme_nom} {l.materiel_nom ? `— ${l.materiel_nom}` : ''} (jusqu'au {fmtDate(effDateFin)})
                              </option>
                            );
                          })}
                        </select>
                        {(() => {
                          const ligneSelectionnee = contrat.lignes_materiels?.find(l => l.ligne_id === avenantData.ligne_id);
                          if (!ligneSelectionnee) return null;
                          const effDateFin = childAvenants
                            .filter(av => av.statut !== 'annule')
                            .flatMap(av => av.lignes_materiels || [])
                            .filter(al => al.gamme_id === ligneSelectionnee.gamme_id && (!ligneSelectionnee.materiel_id || !al.materiel_id || al.materiel_id === ligneSelectionnee.materiel_id))
                            .reduce((max, al) => (al.date_fin > max ? al.date_fin : max), ligneSelectionnee.date_fin);
                          const minDateFin = (() => { const d = new Date(effDateFin); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })();
                          return (
                            <input type="date" value={avenantData.nouvelle_date_fin}
                              onChange={e => setAvenantData(p => ({ ...p, nouvelle_date_fin: e.target.value }))}
                              min={minDateFin}
                              data-testid="avenant-date-fin"
                              className="w-full px-2.5 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }} />
                          );
                        })()}
                        <input value={avenantData.motif} onChange={e => setAvenantData(p => ({ ...p, motif: e.target.value }))}
                          placeholder="Motif (optionnel)" className="w-full px-2.5 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }} />
                        <button onClick={handleAvenant} disabled={saving}
                          className="w-full py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                          style={{ background: '#C2410C' }}>
                          Créer l'avenant
                        </button>
                      </div>
                    )}
                    {childAvenants.length > 0 && (
                      <div className="space-y-1 mt-2">
                        {childAvenants.map(a => {
                          const ligneA = a.lignes_materiels?.[0];
                          const joursSupp = ligneA?.duree_jours;
                          const montantSupp = ligneA?.montant;
                          const dateFin = ligneA?.date_fin;
                          const si = statutInfo(a.statut);
                          return (
                            <button
                              key={a.contrat_id}
                              data-testid={`avenant-row-${a.contrat_id}`}
                              onClick={() => onOpenAvenant && onOpenAvenant(a)}
                              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors hover:opacity-90"
                              style={{ background: 'var(--bg-subtle)', cursor: 'pointer' }}>
                              <RotateCcw size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                              <span className="text-xs font-semibold flex-shrink-0" style={{ color: 'var(--text)' }}>{a.numero}</span>
                              <span className="text-xs flex-1" style={{ color: 'var(--text-muted)' }}>
                                {fmtDate(ligneA?.date_debut)} → {fmtDate(dateFin)}{' '}
                                {(joursSupp > 0 || montantSupp > 0) && (
                                  <span style={{ color: '#C2410C', fontWeight: 600 }}>
                                    ({joursSupp > 0 ? `+${joursSupp} j` : ''}{joursSupp > 0 && montantSupp > 0 ? ', ' : ''}{montantSupp > 0 ? `+${fmtEur(montantSupp)}` : ''})
                                  </span>
                                )}
                              </span>
                              <span className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 font-medium"
                                style={{ background: si.bg, color: si.color }}>
                                {si.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

              </div>
            )}

            {/* ─── Suspensions ─── */}
            {activeTab === 'suspensions' && !isNew && (
              <SuspensionsTab contrat={contrat} onSaved={(updated) => { onSaved(updated); }} />
            )}
            {activeTab === 'suspensions' && isNew && (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-sm"
                style={{ color: 'var(--text-muted)' }}>
                <CloudSnow size={28} style={{ opacity: 0.4 }} />
                <p>Enregistrez d'abord le contrat pour gérer les suspensions</p>
              </div>
            )}

            {/* ─── Notes ─── */}
            {/* ─── Facturation ─── */}
            {activeTab === 'facturation' && (
              <FacturationTab contrat={contrat} onContratUpdate={() => onSaved && api.getContrat(contrat.contrat_id).then(r => onSaved(r.data)).catch(()=>{})} />
            )}

            {activeTab === 'discussions' && contrat?.contrat_id && (
              <div style={{ padding: '12px 0' }}>
                <ActivitesFichePanel
                  record_type="location"
                  record_id={contrat.contrat_parent_id || contrat.contrat_id}
                  record_label={contrat.contrat_parent_id ? (contrat.parent_numero || contrat.contrat_parent_id) : contrat.numero}
                  type_document="location"
                  numero_document={contrat.contrat_parent_id ? (contrat.parent_numero || contrat.contrat_parent_id) : contrat.numero}
                  created_by_nom={contrat.created_by_nom ?? null}
                />
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="space-y-3">
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={8}
                  className="w-full px-3 py-2 rounded-xl border text-sm resize-none"
                  style={{ borderColor: 'var(--border)' }}
                  data-testid="notes-textarea"
                  placeholder="Notes internes..." />
              </div>
            )}

            {/* ─── Documents ─── */}
            {activeTab === 'documents' && (
              <div className="space-y-4">
                {isNew ? (
                  <div className="flex flex-col items-center justify-center py-10 gap-2 rounded-xl border border-dashed"
                    style={{ borderColor: 'var(--border)' }}>
                    <FileText size={28} style={{ color: 'var(--text-disabled)' }} />
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Enregistrez d'abord le contrat pour accéder aux documents</p>
                  </div>
                ) : (
                  <>
                    {/* ─── Pièces générées par Bedee ──────────────────────────── */}
                    {(() => {
                      const rows = [];
                      // Contrat signé
                      if (validation?.signature_recue || contrat.signatures_history?.length > 0) {
                        rows.push({
                          key: 'contrat',
                          typeLabel: 'Contrat signé',
                          typeBg: '#F3F0FF', typeColor: '#7C3AED',
                          numero: contrat.numero,
                          date_enlevement: '—',
                          date_retour: '—',
                          date_debut: contrat.date_debut,
                          date_fin: contrat.date_fin,
                          date_signature: validation?.signed_at || validation?.date_signature,
                          signataire: validation?.signataire_nom,
                          onView: async () => {
                            try {
                              const resp = await api.getContratPdf(contrat.contrat_id);
                              const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                              const w = window.open(url, '_blank');
                              if (w) w.focus();
                              setTimeout(() => URL.revokeObjectURL(url), 30000);
                            } catch { toast.error('Erreur lors du chargement du PDF'); }
                          },
                        });
                      }
                      // BL / BR
                      bls.forEach(bl => {
                        const isBR = bl.type === 'retour';
                        rows.push({
                          key: bl.bl_id,
                          typeLabel: isBR ? 'Bon de retour' : 'Bon de livraison',
                          typeBg: isBR ? '#FFF7ED' : '#EFF6FF',
                          typeColor: isBR ? '#C2410C' : '#1D4ED8',
                          numero: bl.numero,
                          date_enlevement: isBR ? '—' : (bl.date_effective || bl.date_debut),
                          date_retour: isBR ? (bl.date_effective || bl.date_debut || bl.date_fin) : '—',
                          date_debut: '—',
                          date_fin: '—',
                          date_signature: bl.signed_at,
                          signataire: bl.signe_par,
                          statut: bl.statut,
                          onView: async () => {
                            try {
                              const resp = await api.getBLPdf(bl.bl_id);
                              const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                              const w = window.open(url, '_blank');
                              if (w) w.focus();
                              setTimeout(() => URL.revokeObjectURL(url), 30000);
                            } catch { toast.error('Erreur lors du chargement du PDF'); }
                          },
                        });
                      });

                      if (rows.length === 0) return null;

                      const fd = (v) => {
                        if (!v || v === '—') return '—';
                        try {
                          const s = String(v).slice(0, 10);
                          const [y, m, d] = s.split('-');
                          return `${d}/${m}/${y}`;
                        } catch { return '—'; }
                      };

                      const TH = ({ children, className = '' }) => (
                        <th className={`px-3 py-2 text-left text-xs font-semibold whitespace-nowrap ${className}`}
                          style={{ color: 'var(--text-muted)', background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                          {children}
                        </th>
                      );
                      const TD = ({ children, className = '' }) => (
                        <td className={`px-3 py-2 text-xs whitespace-nowrap ${className}`} style={{ color: 'var(--text)', borderBottom: '1px solid var(--border)' }}>
                          {children}
                        </td>
                      );

                      return (
                        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                          <div className="px-4 py-2.5 border-b flex items-center gap-2"
                            style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
                            <FileText size={13} style={{ color: 'var(--primary-solid)' }} />
                            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                              Pièces générées par Bedee ({rows.length})
                            </p>
                          </div>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr>
                                  <TH>Type</TH>
                                  <TH>Numéro</TH>
                                  <TH>Date enlèv.</TH>
                                  <TH>Date retour</TH>
                                  <TH>Date début</TH>
                                  <TH>Date fin</TH>
                                  <TH>Date signature</TH>
                                  <TH>Signataire</TH>
                                  <TH></TH>
                                </tr>
                              </thead>
                              <tbody>
                                {rows.map((row, i) => (
                                  <tr key={row.key}
                                    style={{ background: i % 2 === 0 ? 'white' : 'var(--bg-subtle)' }}>
                                    <TD>
                                      <span className="px-2 py-0.5 rounded-full text-xs font-bold"
                                        style={{ background: row.typeBg, color: row.typeColor }}>
                                        {row.typeLabel}
                                      </span>
                                      {row.statut === 'annule' && (
                                        <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-bold"
                                          style={{ background: '#FEE2E2', color: '#B91C1C', verticalAlign: 'middle' }}>
                                          Annulé
                                        </span>
                                      )}
                                    </TD>
                                    <TD>
                                      <span className="font-mono font-medium" style={{ color: row.statut === 'annule' ? 'var(--text-muted)' : 'var(--text)', textDecoration: row.statut === 'annule' ? 'line-through' : 'none' }}>
                                        {row.numero || '—'}
                                      </span>
                                    </TD>
                                    <TD>{fd(row.date_enlevement)}</TD>
                                    <TD>{fd(row.date_retour)}</TD>
                                    <TD>{fd(row.date_debut)}</TD>
                                    <TD>{fd(row.date_fin)}</TD>
                                    <TD>
                                      {fd(row.date_signature) !== '—'
                                        ? <span style={{ color: '#15803D' }}>{fd(row.date_signature)}</span>
                                        : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                                    </TD>
                                    <TD>
                                      <span className="truncate" style={{ maxWidth: 120, display: 'inline-block', color: 'var(--text-secondary)' }}>
                                        {row.signataire || '—'}
                                      </span>
                                    </TD>
                                    <TD>
                                      <button
                                        data-testid={`visualiser-${row.key}`}
                                        onClick={row.onView}
                                        title="Visualiser"
                                        className="flex items-center justify-center p-1.5 rounded-lg transition-all hover:opacity-80"
                                        style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                                        <Eye size={13} />
                                      </button>
                                    </TD>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })()}

                    {/* ─── Documents joints (pièces déposées) ─────────────────── */}
                    <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                      <div className="px-4 py-2.5 border-b flex items-center gap-2"
                        style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
                        <Upload size={13} style={{ color: 'var(--primary-solid)' }} />
                        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                          Pièces déposées par l'utilisateur{docCount > 0 ? ` (${docCount})` : ''}
                        </p>
                      </div>
                      <div className="p-3">
                        <DocumentsTab
                          entityId={contrat.contrat_id}
                          listFn={() => api.getContratDocumentsTab(contrat.contrat_id)}
                          uploadFn={(fd) => api.uploadContratDocumentTab(contrat.contrat_id, fd)}
                          updateFn={(docId, data) => api.patchContratDocumentTab(contrat.contrat_id, docId, data)}
                          deleteFn={(docId) => api.deleteContratDocumentTab(contrat.contrat_id, docId)}
                          downloadFn={(docId) => api.downloadContratDocumentTab(contrat.contrat_id, docId)}
                          onCountChange={setDocCount}
                          wideColumns
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

          </div>
        </div>

        {/* Actions — toujours visibles */}
        {(canEditMeta || canEditInfosAdmin) && !validation?.signature_en_attente && (
          <div className="flex gap-3 pt-2">
            <button onClick={handleSave} disabled={saving} data-testid="save-contrat-btn"
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: 'var(--primary-gradient)' }}>
              {saving ? 'Enregistrement...' : isNew ? 'Créer le contrat' : 'Enregistrer'}
            </button>
            <button onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-medium border"
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
              Annuler
            </button>
          </div>
        )}

      </div>

      {/* ─── Modale de signature ─── */}
      {showSignatureModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={e => { if (e.target === e.currentTarget) { setShowSignatureModal(false); setSigMode(null); clearCanvas(); } }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
            style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            {/* En-tête */}
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center gap-2">
                <PenTool size={16} style={{ color: 'var(--primary-solid)' }} />
                <span className="font-semibold" style={{ color: 'var(--text)' }}>Signature du contrat {contrat?.numero}</span>
              </div>
              <button onClick={() => { setShowSignatureModal(false); setSigMode(null); clearCanvas(); }}
                className="p-1.5 rounded-lg hover:bg-gray-100">
                <X size={16} style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Nom du signataire */}
              <div>
                <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Nom du signataire</label>
                <input
                  type="text"
                  value={signaireNom}
                  onChange={e => setSignaireNom(e.target.value)}
                  placeholder="Nom complet du signataire"
                  className="w-full px-3 py-2 rounded-xl border text-sm"
                  style={{ borderColor: 'var(--border)' }}
                  data-testid="sig-modal-nom"
                />
              </div>

              {/* Choix de méthode (si pas encore sélectionné) */}
              {!sigMode && (
                <div>
                  <label className="text-xs font-semibold mb-2 block" style={{ color: 'var(--text-muted)' }}>Méthode de signature</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      data-testid="sig-modal-tablette"
                      onClick={() => setSigMode('tablette')}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all hover:border-purple-400 hover:bg-purple-50"
                      style={{ borderColor: '#E9D5FF' }}>
                      <Tablet size={24} style={{ color: '#7C3AED' }} />
                      <span className="text-sm font-semibold" style={{ color: '#7C3AED' }}>Tablette</span>
                      <span className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>Au doigt sur écran tactile</span>
                    </button>
                    <button
                      data-testid="sig-modal-ordinateur"
                      onClick={() => setSigMode('ordinateur')}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all hover:border-green-400 hover:bg-green-50"
                      style={{ borderColor: '#BBF7D0' }}>
                      <Monitor size={24} style={{ color: '#059669' }} />
                      <span className="text-sm font-semibold" style={{ color: '#059669' }}>Ordinateur</span>
                      <span className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>À la souris sur cet écran</span>
                    </button>
                    <button
                      data-testid="sig-modal-mail"
                      onClick={() => setSigMode('mail')}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all hover:border-blue-400 hover:bg-blue-50"
                      style={{ borderColor: '#BFDBFE' }}>
                      <Mail size={24} style={{ color: '#1D4ED8' }} />
                      <span className="text-sm font-semibold" style={{ color: '#1D4ED8' }}>Par mail</span>
                      <span className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>PDF envoyé au signataire</span>
                    </button>
                    <button
                      data-testid="sig-modal-lien"
                      onClick={() => setSigMode('lien')}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all hover:border-orange-400 hover:bg-orange-50"
                      style={{ borderColor: '#FED7AA' }}>
                      <LinkIcon size={24} style={{ color: '#C2410C' }} />
                      <span className="text-sm font-semibold" style={{ color: '#C2410C' }}>Lien en ligne</span>
                      <span className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>Lien envoyé par mail</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Tablette — envoi vers la tablette terrain (sans canvas) */}
              {sigMode === 'tablette' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Tablet size={14} style={{ color: '#7C3AED' }} />
                      <span className="text-sm font-semibold" style={{ color: '#6D28D9' }}>Envoi sur la tablette terrain</span>
                    </div>
                    <button type="button" onClick={() => setSigMode(null)}
                      className="text-xs px-2 py-1 rounded-lg"
                      style={{ background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}>
                      Changer
                    </button>
                  </div>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                    Le contrat sera mis en attente de signature et apparaîtra sur la tablette terrain.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        setSaving(true);
                        try {
                          const res = await api.envoyerSignatureTerrain(contrat.contrat_id, {
                            signataire_nom: signaireNom || contactPrimaire?.nom || client.client_nom || '',
                            signataire_contact_id: contactPrimaire?.id || null,
                          });
                          toast.success('Contrat envoyé en signature terrain');
                          setShowSignatureModal(false);
                          setSigMode(null);
                          onSaved(res.data);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Erreur');
                        } finally { setSaving(false); }
                      }}
                      disabled={saving}
                      data-testid="envoyer-tablette-btn"
                      className="flex-1 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-40"
                      style={{ background: '#7C3AED' }}>
                      {saving ? 'Envoi…' : 'Envoyer sur la tablette terrain'}
                    </button>
                    <button onClick={() => { setShowSignatureModal(false); setSigMode(null); }}
                      className="px-3 py-2 rounded-xl text-sm border"
                      style={{ borderColor: '#C4B5FD', color: '#6B7280' }}>
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              {/* Ordinateur — canvas de dessin */}
              {sigMode === 'ordinateur' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Monitor size={14} style={{ color: '#059669' }} />
                      <span className="text-sm font-semibold" style={{ color: '#059669' }}>Signature ordinateur</span>
                    </div>
                    <button type="button" onClick={() => setSigMode(null)}
                      className="text-xs px-2 py-1 rounded-lg"
                      style={{ background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}>
                      Changer
                    </button>
                  </div>
                  <div style={{ border: '1.5px dashed #6EE7B7', borderRadius: '10px', overflow: 'hidden', position: 'relative', touchAction: 'none', background: '#FAFAFA' }}>
                    <canvas ref={canvasRef} width={500} height={180} data-testid="signature-canvas"
                      style={{ display: 'block', width: '100%', cursor: 'crosshair' }}
                      onMouseDown={e => { setIsDrawing(true); lastDrawPos.current = getDrawPos(e, canvasRef.current); }}
                      onMouseMove={e => { if (!isDrawing) return; const c = canvasRef.current; const ctx = c.getContext('2d'); const p = getDrawPos(e,c); ctx.beginPath(); ctx.moveTo(lastDrawPos.current.x, lastDrawPos.current.y); ctx.lineTo(p.x,p.y); ctx.strokeStyle='#1A3A6E'; ctx.lineWidth=2; ctx.lineCap='round'; ctx.stroke(); lastDrawPos.current=p; setHasSignature(true); }}
                      onMouseUp={() => setIsDrawing(false)} onMouseLeave={() => setIsDrawing(false)}
                      onTouchStart={e => { e.preventDefault(); setIsDrawing(true); lastDrawPos.current = getDrawPos(e, canvasRef.current); }}
                      onTouchMove={e => { e.preventDefault(); if (!isDrawing) return; const c = canvasRef.current; const ctx = c.getContext('2d'); const p = getDrawPos(e,c); ctx.beginPath(); ctx.moveTo(lastDrawPos.current.x, lastDrawPos.current.y); ctx.lineTo(p.x,p.y); ctx.strokeStyle='#1A3A6E'; ctx.lineWidth=2; ctx.lineCap='round'; ctx.stroke(); lastDrawPos.current=p; setHasSignature(true); }}
                      onTouchEnd={() => setIsDrawing(false)}
                    />
                    {!hasSignature && (
                      <p style={{ position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',color:'#6EE7B7',fontSize:'0.8rem',pointerEvents:'none',margin:0 }}>Signez ici</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        if (!hasSignature) { toast.error('Veuillez apposer une signature'); return; }
                        setSaving(true);
                        try {
                          const dataUrl = canvasRef.current.toDataURL('image/png');
                          const res = await api.signerContratTerrain(contrat.contrat_id, {
                            signataire_nom: signaireNom || contactPrimaire?.nom || client.client_nom || '',
                            signature_image_b64: dataUrl,
                            methode: 'ordinateur',
                          });
                          toast.success('Contrat signé');
                          setShowSignatureModal(false);
                          setSigMode(null);
                          clearCanvas();
                          onSaved(res.data);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Erreur');
                        } finally { setSaving(false); }
                      }}
                      disabled={saving || !hasSignature}
                      data-testid="valider-signature-canvas"
                      className="flex-1 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-40"
                      style={{ background: '#059669' }}>
                      {saving ? 'Enregistrement…' : 'Valider la signature'}
                    </button>
                    <button onClick={clearCanvas}
                      className="px-3 py-2 rounded-xl text-sm border"
                      style={{ borderColor: '#6EE7B7', color: '#059669' }}>
                      Effacer
                    </button>
                  </div>
                </div>
              )}

              {/* Formulaire mail / lien */}
              {(sigMode === 'mail' || sigMode === 'lien') && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {sigMode === 'mail' ? <Mail size={14} style={{ color: '#1D4ED8' }} /> : <LinkIcon size={14} style={{ color: '#C2410C' }} />}
                      <span className="text-sm font-semibold"
                        style={{ color: sigMode === 'mail' ? '#1D4ED8' : '#C2410C' }}>
                        {sigMode === 'mail' ? 'Envoi PDF par mail' : 'Lien de signature en ligne'}
                      </span>
                    </div>
                    <button type="button" onClick={() => setSigMode(null)}
                      className="text-xs px-2 py-1 rounded-lg"
                      style={{ background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}>
                      Changer
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>Destinataire *</label>
                    <input
                      type="email"
                      placeholder="email@client.fr"
                      value={sigEmail}
                      onChange={e => setSigEmail(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border text-sm outline-none"
                      style={{ borderColor: sigMode === 'mail' ? '#93C5FD' : '#FDBA74' }}
                      data-testid={sigMode === 'mail' ? 'sig-mail-email' : 'sig-lien-email'}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>Sujet</label>
                    <input
                      type="text"
                      value={sigSujet}
                      onChange={e => setSigSujet(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border text-sm outline-none"
                      style={{ borderColor: 'var(--border)' }}
                      data-testid="sig-mail-sujet"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>Corps du message</label>
                    <textarea
                      value={sigCorps}
                      onChange={e => setSigCorps(e.target.value)}
                      rows={5}
                      className="w-full px-3 py-2 rounded-xl border text-sm outline-none resize-none"
                      style={{ borderColor: 'var(--border)' }}
                      data-testid="sig-mail-corps"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={sigMode === 'mail' ? handleSignatureMail : handleSignatureLien}
                      disabled={saving || !sigEmail}
                      data-testid={sigMode === 'mail' ? 'sig-mail-submit' : 'sig-lien-submit'}
                      className="flex-1 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-40"
                      style={{ background: sigMode === 'mail' ? '#1D4ED8' : '#C2410C' }}>
                      {saving ? 'Envoi…' : sigMode === 'mail' ? 'Envoyer le PDF' : 'Envoyer le lien'}
                    </button>
                    <button onClick={() => { setShowSignatureModal(false); setSigMode(null); }}
                      className="px-3 py-2 rounded-xl text-sm border"
                      style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    {/* ─── Modale envoi devis par email — rendue DANS le SidePanel (position:fixed) pour éviter les restrictions pointer-events de Radix ─── */}
    {mailDevisModal && (
      <div className="fixed inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.45)', zIndex: 10000 }}
        onMouseDown={e => { if (e.target === e.currentTarget) setMailDevisModal(false); }}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 p-8 space-y-5"
          onMouseDown={e => e.stopPropagation()}>
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold" style={{ color: 'var(--text)' }}>Envoyer le devis par email</h3>
            <button onClick={() => setMailDevisModal(false)}><X size={16} style={{ color: 'var(--text-muted)' }} /></button>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>Destinataire *</label>
              <input type="text" value={mailDevisForm.destinataire}
                onChange={e => setMailDevisForm(f => ({ ...f, destinataire: e.target.value }))}
                placeholder="email@client.fr, autre@client.fr"
                data-testid="mail-devis-destinataire"
                className="w-full px-3 py-2 rounded-xl border text-sm outline-none"
                style={{ borderColor: 'var(--border)' }} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>Sujet</label>
              <input type="text" value={mailDevisForm.sujet}
                onChange={e => setMailDevisForm(f => ({ ...f, sujet: e.target.value }))}
                data-testid="mail-devis-sujet"
                className="w-full px-3 py-2 rounded-xl border text-sm outline-none"
                style={{ borderColor: 'var(--border)' }} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>Corps du message</label>
              <textarea value={mailDevisForm.corps}
                onChange={e => setMailDevisForm(f => ({ ...f, corps: e.target.value }))}
                rows={7}
                data-testid="mail-devis-corps"
                className="w-full px-3 py-2 rounded-xl border text-sm outline-none resize-none"
                style={{ borderColor: 'var(--border)' }} />
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button onClick={() => setMailDevisModal(false)}
              className="px-4 py-2 rounded-xl text-sm border"
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
              Annuler
            </button>
            <button onClick={handleSendMailDevis} disabled={sendingMailDevis || !mailDevisForm.destinataire}
              data-testid="mail-devis-envoyer"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: '#1D4ED8' }}>
              {sendingMailDevis ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              {sendingMailDevis ? 'Envoi…' : 'Envoyer'}
            </button>
          </div>
        </div>
      </div>
    )}
    {/* ── Modale "Signature reçue" ── */}
    {showSignatureRecueModal && (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.5)' }}
        onClick={e => { if (e.target === e.currentTarget) { setShowSignatureRecueModal(false); setSignatureRecuePdf(null); } }}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          {/* En-tête */}
          <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-2">
              <CheckCircle size={16} style={{ color: '#059669' }} />
              <span className="font-semibold" style={{ color: 'var(--text)' }}>Signature reçue — {contrat?.numero}</span>
            </div>
            <button onClick={() => { setShowSignatureRecueModal(false); setSignatureRecuePdf(null); }}
              className="p-1.5 rounded-lg hover:bg-gray-100">
              <X size={16} style={{ color: 'var(--text-muted)' }} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Confirmez la réception du contrat signé par le client. Vous pouvez déposer le PDF signé ou confirmer sans pièce jointe.
            </p>

            {/* Zone upload PDF optionnel */}
            <div>
              <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-muted)' }}>
                PDF signé (optionnel)
              </label>
              {signatureRecuePdf ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl border"
                  style={{ borderColor: '#86EFAC', background: '#F0FDF4' }}>
                  <FileText size={14} style={{ color: '#059669' }} />
                  <span className="text-sm font-medium flex-1 truncate" style={{ color: '#065F46' }}>
                    {signatureRecuePdf.name}
                  </span>
                  <button onClick={() => setSignatureRecuePdf(null)}
                    className="p-0.5 rounded hover:bg-green-100">
                    <X size={13} style={{ color: '#DC2626' }} />
                  </button>
                </div>
              ) : (
                <label
                  data-testid="upload-signature-recue"
                  className="flex flex-col items-center gap-2 px-4 py-5 rounded-xl border-2 border-dashed cursor-pointer transition-colors hover:border-green-400 hover:bg-green-50"
                  style={{ borderColor: 'var(--border)' }}>
                  <Upload size={20} style={{ color: 'var(--text-muted)' }} />
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Cliquez pour sélectionner un PDF</span>
                  <input type="file" accept="application/pdf" className="hidden"
                    onChange={e => setSignatureRecuePdf(e.target.files?.[0] || null)} />
                </label>
              )}
            </div>

            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Sans PDF : le contrat sera confirmé sans archivage du document signé. Vous pourrez l'ajouter ultérieurement via l'onglet Documents.
            </p>
          </div>

          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t" style={{ borderColor: 'var(--border)' }}>
            <button onClick={() => { setShowSignatureRecueModal(false); setSignatureRecuePdf(null); }}
              className="px-4 py-2 rounded-xl text-sm font-medium border"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
              Annuler
            </button>
            <button
              data-testid="btn-confirmer-signature-recue"
              onClick={handleSignatureRecue}
              disabled={savingSignatureRecue}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: '#059669' }}>
              {savingSignatureRecue ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              {savingSignatureRecue ? 'Confirmation…' : 'Confirmer la signature'}
            </button>
          </div>
        </div>
      </div>
    )}
    </SidePanel>

    {showBlocageModal && (
      <BlocageClientModal
        blocage={blocageClient}
        entrepriseId={contrat?.client_entity_id || contrat?.client_id}
        onClose={() => setShowBlocageModal(false)}
      />
    )}
  </>
  );
} 

// ─── Page principale ───────────────────────────────────────────────────────────

const STATUT_TABS = [
  { value: null, label: 'Tous' },
  ...STATUTS,
];

// Colonnes avec tri côté serveur vs côté client
const SERVER_SORT_KEYS = new Set(['numero', 'client_nom', 'titre', 'total_ht', 'statut']);
const CLIENT_SORT_KEYS = new Set(['materiels', 'dates', 'facturation']);

export default function Locations() {
  const { activeSociete, activeAgences, isReadOnly } = useSociete();

  const [searchParams] = useSearchParams();
  const [contrats, setContrats]         = useState([]);
  const [total, setTotal]               = useState(0);
  const [sumHt, setSumHt]               = useState(null); // total HT des contrats filtrés
  const [loading, setLoading]           = useState(true);
  const [gammes, setGammes]             = useState([]);
  const [articlesLoc, setArticlesLoc]   = useState([]);
  const [tvaRates, setTvaRates]         = useState([]);

  const now = new Date();
  const [search, setSearch]             = useState('');
  const [filterStatut, setFilterStatut]   = useState(null);
  const [filterAFacturer, setFilterAFacturer] = useState(false);
  const [filterMois, setFilterMois]     = useState(null);   // 1-12 ou null
  const [filterAnnee, setFilterAnnee]   = useState(now.getFullYear());
  const [page, setPage]                 = useState(1);
  const [panelOpen, setPanelOpen]       = useState(false);
  const [selectedContrat, setSelectedContrat] = useState(null);
  const [prefillLigne, setPrefillLigne] = useState(null);
  const [counts, setCounts]             = useState({});
  const [sortConfig, setSortConfig]     = useState({ key: null, direction: 'desc' });

  const PAGE_SIZE = 50;

  // Colonnes avec tri côté serveur
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (search)              params.q         = search;
      if (filterStatut)        params.statut    = filterStatut;
      if (filterAFacturer)     params.a_facturer = true;
      if (filterMois)        { params.mois = filterMois; params.annee = filterAnnee; }
      if (sortConfig.key && SERVER_SORT_KEYS.has(sortConfig.key)) {
        params.sort_by  = sortConfig.key;
        params.sort_dir = sortConfig.direction;
      }
      const [res, statsRes] = await Promise.all([
        api.getContrats(params),
        api.getContratsStats(),
      ]);
      setContrats(res.data.items || []);
      setTotal(res.data.total || 0);
      setSumHt(res.data.sum_ht ?? null);
      setCounts(statsRes.data || {});
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [page, search, filterStatut, filterAFacturer, filterMois, filterAnnee, activeAgences, sortConfig]); // eslint-disable-line

  const loadMeta = useCallback(async () => {
    try {
      const [gamRes, artRes, statsRes, tvaRes] = await Promise.all([
        api.getLocationGammes(),
        api.getArticles({ type: 'prestation', slim: true }),
        api.getContratsStats(),
        api.getTvaRates(),
      ]);
      setGammes(gamRes.data || []);
      setArticlesLoc(artRes.data?.items || artRes.data || []);
      setCounts(statsRes.data || {});
      setTvaRates(tvaRes.data || []);
    } catch {}
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { load(); }, [load]);

  // Auto-ouverture du panneau si ?contrat_id=xxx dans l'URL
  useEffect(() => {
    const cid = searchParams.get('contrat_id');
    if (!cid) return;
    api.getContrat(cid)
      .then(r => {
        if (r.data) { setSelectedContrat(r.data); setPanelOpen(true); }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-ouverture "Nouveau contrat" pré-rempli depuis le Planning (?new=1&...)
  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    const gamme_id   = searchParams.get('gamme_id') || '';
    const gamme_nom  = searchParams.get('gamme_nom') || '';
    const materiel_id = searchParams.get('materiel_id') || null;
    const date_debut = searchParams.get('date_debut') || '';
    if (gamme_id && date_debut) {
      setPrefillLigne({ gamme_id, materiel_id, date_debut });
    }
    setSelectedContrat(null);
    setPanelOpen(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search
  const searchTimer = useRef(null);
  const handleSearch = (v) => {
    setSearch(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setPage(1), 400);
  };

  const openContrat = async (c) => {
    try {
      const res = await api.getContrat(c.contrat_id);
      setSelectedContrat(res.data);
      setPanelOpen(true);
    } catch { toast.error('Erreur'); }
  };

  const openNew = () => {
    if (isReadOnly) { toast.error('Sélectionnez une seule agence pour créer un contrat.'); return; }
    setSelectedContrat(null); setPanelOpen(true);
  };

  const hasActive = filterStatut || filterAFacturer || search || filterMois;
  const pages = Math.ceil(total / PAGE_SIZE);

  const handleSort = (key) => {
    setSortConfig(prev => {
      const newDir = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction: newDir };
    });
    setPage(1);
  };

  const sortedContrats = useMemo(() => {
    if (!sortConfig.key || !CLIENT_SORT_KEYS.has(sortConfig.key)) return contrats;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...contrats].sort((a, b) => {
      if (sortConfig.key === 'materiels') {
        const mA = (a.lignes_materiels || []).map(l => l.materiel_modele).filter(Boolean).join(', ');
        const mB = (b.lignes_materiels || []).map(l => l.materiel_modele).filter(Boolean).join(', ');
        return dir * mA.localeCompare(mB, 'fr');
      }
      if (sortConfig.key === 'dates') {
        const dA = (a.lignes_materiels || []).reduce((m, l) => (!m || l.date_debut < m) ? l.date_debut : m, null) || '';
        const dB = (b.lignes_materiels || []).reduce((m, l) => (!m || l.date_debut < m) ? l.date_debut : m, null) || '';
        return dir * dA.localeCompare(dB);
      }
      if (sortConfig.key === 'facturation') {
        const fA = a.derniere_periode_fin || '';
        const fB = b.derniere_periode_fin || '';
        return dir * fA.localeCompare(fB);
      }
      return 0;
    });
  }, [contrats, sortConfig]); // eslint-disable-line

  // Helper icône de tri
  const SortIcon = ({ colKey }) => {
    if (sortConfig.key !== colKey) return <ArrowUpDown size={12} style={{ color: 'var(--text-disabled)', marginLeft: 4, flexShrink: 0 }} />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp size={12} style={{ color: 'var(--primary-solid)', marginLeft: 4, flexShrink: 0 }} />
      : <ArrowDown size={12} style={{ color: 'var(--primary-solid)', marginLeft: 4, flexShrink: 0 }} />;
  };

  return (
    <div className="flex flex-col h-full gap-5" style={{  }}>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{  color: 'var(--text)' }}>
            Contrats de location
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {total} contrat{total > 1 ? 's' : ''}
          </p>
        </div>
        <button onClick={openNew} data-testid="new-contrat-btn"
          disabled={isReadOnly}
          title={isReadOnly ? 'Sélectionnez une seule agence pour créer un contrat' : undefined}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: 'var(--primary-gradient)' }}>
          <Plus size={16} /> Nouveau contrat
        </button>
      </div>

      {/* Cards statut — style Ventes */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {[{ value: null, label: 'Tous', color: 'var(--text-muted)', bg: 'var(--bg-subtle)', border: 'var(--border)' }, ...STATUTS.filter(s => s.value !== 'facture')].map(s => {
          const isActive = filterStatut === s.value;
          const n = s.value === null
            ? Object.values(counts).reduce((a, b) => a + b, 0)
            : (counts[s.value] || 0);
          return (
            <button key={String(s.value)}
              data-testid={`filter-statut-${s.value ?? 'tous'}`}
              onClick={() => { setFilterStatut(prev => prev === s.value ? null : s.value); setPage(1); }}
              className="rounded-xl p-4 text-left border transition-all hover:shadow-md"
              style={{
                background: isActive ? (s.bg || 'var(--bg-subtle)') : 'white',
                borderColor: isActive ? (s.color || 'var(--primary-solid)') : 'var(--border)',
              }}>
              <div className="text-2xl font-bold" style={{ color: s.color || 'var(--text-muted)' }}>{n}</div>
              <div className="text-xs font-medium mt-0.5" style={{ color: 'var(--text-secondary)' }}>{s.label}</div>
            </button>
          );
        })}
      </div>

      {/* Barre de recherche — style Ventes */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl border bg-white flex-1 min-w-48"
          style={{ borderColor: 'var(--border)' }}>
          <Search size={14} style={{ color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => handleSearch(e.target.value)}
            placeholder="N° contrat, client…"
            data-testid="search-contrats"
            className="flex-1 text-sm focus:outline-none bg-transparent" style={{ color: 'var(--text)' }} />
          {search && <button onClick={() => handleSearch('')}><X size={12} style={{ color: 'var(--text-muted)' }} /></button>}
        </div>
        <button
          onClick={() => { setFilterAFacturer(prev => !prev); setPage(1); }}
          data-testid="toggle-a-facturer"
          className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition-all"
          style={{
            background:   filterAFacturer ? 'var(--primary-solid)' : 'white',
            color:        filterAFacturer ? 'white'                 : 'var(--text-muted)',
            borderColor:  filterAFacturer ? 'var(--primary-solid)'  : 'var(--border)',
          }}>
          <Receipt size={14} />
          À facturer
        </button>

        {/* Navigateur mois/année — sur la même ligne */}
        <div className="flex items-center gap-1 rounded-xl border bg-white px-1 py-1"
          style={{ borderColor: filterMois ? 'var(--primary-solid)' : 'var(--border)' }}>
          <button
            onClick={() => {
              if (!filterMois) { setFilterMois(now.getMonth() + 1); return; }
              let m = filterMois - 1, y = filterAnnee;
              if (m < 1) { m = 12; y -= 1; }
              setFilterMois(m); setFilterAnnee(y); setPage(1);
            }}
            className="p-1 rounded-lg hover:bg-gray-50 transition-colors"
            style={{ color: 'var(--text-muted)' }}>
            <ChevronLeft size={14} />
          </button>
          <select
            value={filterMois ?? ''}
            onChange={e => { setFilterMois(e.target.value ? Number(e.target.value) : null); setPage(1); }}
            className="text-xs font-semibold bg-transparent focus:outline-none cursor-pointer"
            style={{ color: filterMois ? 'var(--primary-solid)' : 'var(--text-muted)', minWidth: 70 }}>
            <option value="">Tous mois</option>
            {['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'].map((m, i) => (
              <option key={i+1} value={i+1}>{m}</option>
            ))}
          </select>
          <span className="text-xs font-bold" style={{ color: filterMois ? 'var(--text)' : 'var(--text-muted)' }}>
            {filterAnnee}
          </span>
          <button
            onClick={() => {
              if (!filterMois) { setFilterMois(now.getMonth() + 1); return; }
              let m = filterMois + 1, y = filterAnnee;
              if (m > 12) { m = 1; y += 1; }
              setFilterMois(m); setFilterAnnee(y); setPage(1);
            }}
            className="p-1 rounded-lg hover:bg-gray-50 transition-colors"
            style={{ color: 'var(--text-muted)' }}>
            <ChevronRight size={14} />
          </button>
        </div>

        <button onClick={load} className="p-2 rounded-xl border hover:bg-gray-50 transition-colors"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          <RefreshCw size={14} />
        </button>

        {hasActive && (
          <button onClick={() => { setFilterStatut(null); setFilterAFacturer(false); setSearch(''); setFilterMois(null); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold"
            style={{ color: '#EF4444', background: '#FEF2F2' }}>
            <X size={12} /> Réinitialiser
          </button>
        )}
      </div>

      {/* Total HT — visible uniquement si filtre date actif */}
      {filterMois && sumHt !== null && (
        <div className="flex items-center justify-end gap-2 -mt-1 px-1">
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Total HT ({total} contrat{total > 1 ? 's' : ''}) :
          </span>
          <span className="text-sm font-bold" style={{ color: 'var(--primary-solid)' }}>
            {sumHt.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
          </span>
        </div>
      )}

      {/* Tableau */}
      <div className="flex-1 rounded-2xl border overflow-hidden flex flex-col" style={{ borderColor: 'var(--border)', background: 'white' }}>
        <div className="overflow-auto flex-1">
          <table className="w-full" style={{ tableLayout: 'fixed' }}>
            <thead className="sticky top-0 z-10">
              <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                {[
                  { label: 'Numéro',     sortKey: 'numero',     w: '11%',  center: false },
                  { label: 'Client',     sortKey: 'client_nom', w: '17%',  center: false },
                  { label: 'Titre',      sortKey: 'titre',      w: '12%',  center: false },
                  { label: 'Matériels',  sortKey: 'materiels',  w: '12%',  center: false },
                  { label: 'Dates',      sortKey: 'dates',      w: '10%',  center: false },
                  { label: 'Montant HT', sortKey: 'total_ht',   w: '8%',   center: true  },
                  { label: 'Statut',     sortKey: 'statut',     w: '6%',   center: true  },
                  { label: 'Facturation',sortKey: 'facturation',w: '6%',   center: true  },
                  { label: 'Suivi par',  sortKey: null,         w: '7%',   center: false },
                ].map(h => (
                  <th key={h.label}
                    onClick={() => handleSort(h.sortKey)}
                    className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide cursor-pointer select-none transition-colors hover:bg-gray-100 ${h.center ? 'text-center' : 'text-left'}`}
                    style={{ color: sortConfig.key === h.sortKey ? 'var(--primary-solid)' : 'var(--text-muted)', width: h.w, background: 'var(--bg-subtle)' }}>
                    <span className={`inline-flex items-center gap-0 ${h.center ? 'justify-center w-full' : ''}`}>
                      {h.label}
                      <SortIcon colKey={h.sortKey} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Chargement...</td></tr>
              ) : sortedContrats.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                  {hasActive ? 'Aucun résultat' : 'Aucun contrat — créez-en un'}
                </td></tr>
              ) : sortedContrats.map(c => {
                const s = statutInfo(c.statut);
                const lignesMat = Array.isArray(c.lignes_materiels) ? c.lignes_materiels : [];
                const nbMat = lignesMat.length || 0;
                const dateMin = lignesMat.reduce((m, l) => (!m || l.date_debut < m) ? l.date_debut : m, null);
                const dateMax = lignesMat.reduce((m, l) => (!m || l.date_fin > m) ? l.date_fin : m, null);
                return (
                  <tr key={c.contrat_id}
                    onClick={() => openContrat(c)}
                    data-testid={`contrat-row-${c.contrat_id}`}
                    className="border-b cursor-pointer hover:bg-blue-50/40 transition-colors"
                    style={{ borderColor: 'var(--border-light)' }}>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold whitespace-nowrap" style={{ color: 'var(--primary-solid)' }}>{c.numero}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm" style={{ color: 'var(--text)' }}>{c.client_nom || '—'}</span>
                    </td>
                    <td className="px-4 py-3" style={{ maxWidth: '160px' }}>
                      <span className="text-sm block truncate" style={{ color: 'var(--text)' }} title={c.titre || ''}>{c.titre || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                        {(() => {
                          const modeles = lignesMat.map(l => l.materiel_modele).filter(Boolean);
                          return modeles.length > 0
                            ? <span title={modeles.join(', ')} className="block truncate" style={{ maxWidth: '200px' }}>{modeles.join(', ')}</span>
                            : '—';
                        })()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                        {fmtDateDMY(dateMin)} – {fmtDateDMY(dateMax)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{fmtEur(c.total_ht)}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold"
                        style={{ background: s.bg, color: s.color }}>
                        {s.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {c.statut === 'retour' ? (
                        (() => {
                          const hasUnbilledAv = c.has_unbilled_avenants;
                          return c.nb_factures > 0 && !hasUnbilledAv ? (
                            <span data-testid={`badge-fac-complet-${c.contrat_id}`}
                              className="px-2.5 py-1 rounded-full text-xs font-semibold"
                              style={{ background: '#DCFCE7', color: '#15803D' }}>
                              Facturé ✓
                            </span>
                          ) : (
                            <span data-testid={`badge-fac-attente-${c.contrat_id}`}
                              className="px-2.5 py-1 rounded-full text-xs font-semibold"
                              style={{ background: '#FEF3C7', color: '#B45309' }}>
                              À facturer
                            </span>
                          );
                        })()
                      ) : (c.statut === 'en_cours' || c.statut === 'confirme') && c.derniere_periode_fin ? (
                        <span data-testid={`badge-fac-partiel-${c.contrat_id}`}
                          className="px-2.5 py-1 rounded-full text-xs font-semibold"
                          style={{ background: '#FEF3C7', color: '#B45309' }}>
                          {new Date(c.derniere_periode_fin + 'T12:00:00').toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                        </span>
                      ) : (
                        <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                      {(c.created_by_nom || '').split(' ')[0] || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: 'var(--border)' }}>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Page {page} / {pages} — {total} résultats
            </span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="w-8 h-8 rounded-lg border flex items-center justify-center disabled:opacity-40"
                style={{ borderColor: 'var(--border)' }}>
                <ChevronRight size={14} className="rotate-180" />
              </button>
              <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
                className="w-8 h-8 rounded-lg border flex items-center justify-center disabled:opacity-40"
                style={{ borderColor: 'var(--border)' }}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Side panel */}
      {panelOpen && (
        <ContratPanel
          key={selectedContrat?.contrat_id || 'new'}
          contrat={selectedContrat}
          gammes={gammes}
          articlesLocation={articlesLoc}
          tvaRates={tvaRates}
          prefillLigne={!selectedContrat ? prefillLigne : null}
          onClose={() => { setPanelOpen(false); setSelectedContrat(null); setPrefillLigne(null); }}
          onSaved={(newContrat) => { if (newContrat) setSelectedContrat(newContrat); load(); }}
          onOpenAvenant={openContrat}
        />
      )}
    </div>
  );
}
